/**
 * The Modeler document: one Y.Doc per model, parameters only — never geometry.
 * Top-level maps: meta, elements, types, assemblies, materials, levels, views, sheets, settings.
 * Element/type/... records are Y.Maps; an element's `params` is a NESTED Y.Map keyed per
 * param (concurrent edits to different params never conflict). Path2D values are plain
 * objects inside that map — replaced atomically (01-architecture.md §3.1).
 */
import { normalizeScope, SCOPE_KEY } from './scope'
import { wallScopeAfter, withoutSheathing } from './wallScopeAfter'
import { nicheSpec } from './niche'
import { awningSpec, AWNING_FABRIC_MATERIAL_NAME } from './awning'
import { applyObjectInstanceSize } from './categories/object/objectKinds'
import { isGateOp } from './categories/openingOps'
const GATE_STYLE_VALUES = ['picketOverPanel', 'picket', 'solid', 'slat', 'vPlank']
import * as Y from 'yjs'
import type {
  AssemblyRecord, ElementId, ElementRecord, LevelId, LevelRecord, MaterialRecord,
  MaterialId, AssemblyId, ModelMeta, ParamValue, TypeId, TypeRecord, CategoryId,
  SheetId, SheetRecord, ViewId, ViewRecord, DraftingId, DraftingRecord,
  AnnoTypeId, AnnoTypeRecord, GridId, GridRecord, PhaseId, PhaseRecord,
  TapeNoteId, TapeNoteRecord, RoomId, RoomRecord, RoomSeparatorId, RoomSeparatorRecord,
  DraftLayerId, DraftLayerRecord, CircuitId, CircuitRecord, SiteId, SiteRecord, DownspoutId, DownspoutRecord,
} from './types'
import { mergeAndResolve, normalizeStructural, normalizeWindowRake, resolveSchemaParams, schemaParams, type ResolvedElement , assemblyThickness } from './params'
import { foldCaseworkStyle, hasFaceFrame, caseworkBoxDepthFt, styleMapOf } from './caseworkStyle'
import { rowOfParams, rowKey, rowLevels, ROW_BASE_MOLDING_IN, type RunRowSetting } from './caseworkRows'
import { plumbingStyleOf, plumbingWidthIn } from './categories/object/plumbingStyles'
import { getCategory } from './registry'
import { ROOF_TILE_MATERIAL_NAMES, roofingSpecOf } from './roofTiles'
import { applyWallFinishes, claddingSpecOf } from './wallCladding'
import { normalizeSidingSettings } from './sidingSettings'
import { wallBandSteps, faceBands } from './wallFaceSplits'
import { resolveFacadeSplits, wallZSpan } from './facadeLines'
import { validFinishLayout } from './finishLayout'
import { getFinishDefaults, roomFloorKind } from './finishDefaults'
import { normalizeRoomZones } from './roomFinishes'
import { faceSweepsOf, sweepProfileOf } from './wallSweeps'
import { SHUTTER_MATERIAL_NAMES } from './shutters'
import { DEFAULT_MASONRY_UNIT } from './masonry'
import { resolveCopingStyle, COPING_MATERIAL_NAMES } from './coping'
import { roofBearingWalls } from './roofBearingWalls'
import { surfaceHex, metalsHex, metalsBareMaterial, tinted, paintableFinish, type PaletteRoleId } from './colors'
import { lightStyleOf, lightBodyMaterialId, HANGING_LIGHT_KINDS } from './categories/fixture/lightStyles'
import { UNHOSTED_CEILING_LIGHT_FT } from './categories/fixture/fixtureKinds'
import { isStyledPlumbingKind, PLUMBING_METAL_MATERIAL_ID } from './categories/object/plumbingStyles'
import { applyOpeningStyle, applyDoorStyle, normalizeOpeningStyle, frameSystemTwoColor, projectDoorHeightFt } from './openingStyle'
import { isHardwareLook, isDoorHardwareFinish, defaultHardwareLook, hardwareLookSpec, resolveDoorHardware } from './doorHardware'
import { normalizeTrimStyle, applyOpeningTrimStyle, applyRoofEaveStyle, applyInteriorDoorFrame, applyRoofStandards, type EaveStyle } from './trimStyle'
import { normalizeStyleSets, STYLE_SETS_KEY, styleSetSignature, setForElement, currentResolvePhase, type StyleSet, type StyleSetsSettings } from './styleSets'
import { paramsAtPhase, changePhaseKey, normalizePhaseChanges, PHASE_CHANGES_KEY } from './phaseChanges'
import { boxBeamFinishOf, boxBeamFinishMaterialId, DEFAULT_BOX_BEAM_FINISH } from './boxBeams'
import { flattenPathPts as flattenCounterWallPts } from './arc'
import { projectToPolyline as projectCounterWallPt } from './pathOps'

/** the firm's default gutter + downspout finish (firm library record) */
export const GUTTER_MATERIAL_FIRM_ID = 'gutAlum01'
export const GUTTER_MATERIAL_NAME = 'Gutters & Downspouts — Painted Aluminum'
import type { Pt2 as BearingPt2 } from './types'
import type { FaceSplitEntry } from './types'

export interface ModelDoc {
  ydoc: Y.Doc
  /** transaction origin for THIS session — the UndoManager tracks only this origin */
  origin: object
}

export const TOP_MAPS = [
  'meta', 'elements', 'types', 'assemblies', 'materials', 'levels', 'views', 'sheets', 'settings',
  'drafting', 'annoTypes', 'grids', 'phases', 'tapeNotes', 'rooms', 'roomSeparators', 'draftLayers',
  'site', 'downspouts', 'circuits',
] as const
export type TopMapName = (typeof TOP_MAPS)[number]

export function maps(model: ModelDoc) {
  const d = model.ydoc
  return {
    meta: d.getMap<ParamValue>('meta'),
    elements: d.getMap<Y.Map<unknown>>('elements'),
    types: d.getMap<Y.Map<unknown>>('types'),
    assemblies: d.getMap<Y.Map<unknown>>('assemblies'),
    materials: d.getMap<Y.Map<unknown>>('materials'),
    levels: d.getMap<Y.Map<unknown>>('levels'),
    views: d.getMap<Y.Map<unknown>>('views'),
    sheets: d.getMap<Y.Map<unknown>>('sheets'),
    settings: d.getMap<unknown>('settings'),
    drafting: d.getMap<Y.Map<unknown>>('drafting'),
    annoTypes: d.getMap<Y.Map<unknown>>('annoTypes'),
    grids: d.getMap<Y.Map<unknown>>('grids'),
    phases: d.getMap<Y.Map<unknown>>('phases'),
    tapeNotes: d.getMap<Y.Map<unknown>>('tapeNotes'),
    rooms: d.getMap<Y.Map<unknown>>('rooms'),
    roomSeparators: d.getMap<Y.Map<unknown>>('roomSeparators'),
    draftLayers: d.getMap<Y.Map<unknown>>('draftLayers'),
    site: d.getMap<Y.Map<unknown>>('site'),
    downspouts: d.getMap<Y.Map<unknown>>('downspouts'),
    // ATMOSPHERE VIEW Stage 4: electrical circuits (types.ts#CircuitRecord)
    circuits: d.getMap<Y.Map<unknown>>('circuits'),
  }
}

/**
 * DIMENSION EQ round: standing rules re-applied at the end of every OUTERMOST local
 * transaction, inside it — so an edit and the rule's follow-up move are one undo step
 * (undo itself never runs these; it restores the prior, already-consistent state).
 */
type PostTransactHook = (model: ModelDoc, tr?: Y.Transaction) => void
const postTransactHooks: PostTransactHook[] = []
/** hooks that must see every other hook's writes — MODEL PRECISION's lattice snap
 *  (precision.ts) runs here, so a standing rule's follow-up move is snapped too */
const lastTransactHooks: PostTransactHook[] = []
export function registerPostTransactHook(fn: PostTransactHook, opts?: { last?: boolean }): void {
  const list = opts?.last ? lastTransactHooks : postTransactHooks
  if (!list.includes(fn)) list.push(fn)
}
let transactDepth = 0

export function transact(model: ModelDoc, fn: () => void): void {
  model.ydoc.transact((tr) => {
    transactDepth++
    try {
      fn()
      if (transactDepth === 1) {
        for (const h of postTransactHooks) h(model, tr)
        for (const h of lastTransactHooks) h(model, tr)
      }
    } finally {
      transactDepth--
    }
  }, model.origin)
}

// ---------- record <-> Y.Map ----------

/** Write a plain record into a fresh Y.Map; `params` becomes a nested Y.Map. */
export function recordToYMap(rec: Record<string, unknown>): Y.Map<unknown> {
  const m = new Y.Map<unknown>()
  for (const [k, v] of Object.entries(rec)) {
    if (v === undefined) continue
    if (k === 'params') {
      const pm = new Y.Map<unknown>()
      for (const [pk, pv] of Object.entries(v as Record<string, unknown>)) {
        if (pv !== undefined) pm.set(pk, pv)
      }
      m.set('params', pm)
    } else {
      m.set(k, v)
    }
  }
  return m
}

function yMapToRecord<T>(m: Y.Map<unknown> | undefined): T | null {
  if (!m) return null
  const out: Record<string, unknown> = {}
  m.forEach((v, k) => {
    if (k === 'params' && v instanceof Y.Map) {
      const params: Record<string, unknown> = {}
      v.forEach((pv, pk) => { params[pk] = pv })
      out.params = params
    } else {
      out[k] = v
    }
  })
  return out as T
}

// ---------- typed reads ----------

export const getElement = (model: ModelDoc, id: ElementId): ElementRecord | null =>
  yMapToRecord<ElementRecord>(maps(model).elements.get(id))
export const getType = (model: ModelDoc, id: TypeId): TypeRecord | null =>
  yMapToRecord<TypeRecord>(maps(model).types.get(id))
export const getAssembly = (model: ModelDoc, id: AssemblyId): AssemblyRecord | null =>
  yMapToRecord<AssemblyRecord>(maps(model).assemblies.get(id))
export const getMaterial = (model: ModelDoc, id: MaterialId): MaterialRecord | null =>
  yMapToRecord<MaterialRecord>(maps(model).materials.get(id))
export const getLevel = (model: ModelDoc, id: LevelId): LevelRecord | null =>
  yMapToRecord<LevelRecord>(maps(model).levels.get(id))
export const getView = (model: ModelDoc, id: ViewId): ViewRecord | null =>
  yMapToRecord<ViewRecord>(maps(model).views.get(id))
export const getSheet = (model: ModelDoc, id: SheetId): SheetRecord | null =>
  yMapToRecord<SheetRecord>(maps(model).sheets.get(id))
export const getDrafting = (model: ModelDoc, id: DraftingId): DraftingRecord | null =>
  yMapToRecord<DraftingRecord>(maps(model).drafting.get(id))
export const getAnnoType = (model: ModelDoc, id: AnnoTypeId): AnnoTypeRecord | null =>
  yMapToRecord<AnnoTypeRecord>(maps(model).annoTypes.get(id))
export const getGrid = (model: ModelDoc, id: GridId): GridRecord | null =>
  yMapToRecord<GridRecord>(maps(model).grids.get(id))
export const getPhase = (model: ModelDoc, id: PhaseId): PhaseRecord | null =>
  yMapToRecord<PhaseRecord>(maps(model).phases.get(id))
export const getTapeNote = (model: ModelDoc, id: TapeNoteId): TapeNoteRecord | null =>
  yMapToRecord<TapeNoteRecord>(maps(model).tapeNotes.get(id))
export const getRoom = (model: ModelDoc, id: RoomId): RoomRecord | null =>
  yMapToRecord<RoomRecord>(maps(model).rooms.get(id))
export const getRoomSeparator = (model: ModelDoc, id: RoomSeparatorId): RoomSeparatorRecord | null =>
  yMapToRecord<RoomSeparatorRecord>(maps(model).roomSeparators.get(id))
export const getDraftLayer = (model: ModelDoc, id: DraftLayerId): DraftLayerRecord | null =>
  yMapToRecord<DraftLayerRecord>(maps(model).draftLayers.get(id))
export const getSiteRecord = (model: ModelDoc, id: SiteId): SiteRecord | null =>
  yMapToRecord<SiteRecord>(maps(model).site.get(id))
export const getDownspout = (model: ModelDoc, id: DownspoutId): DownspoutRecord | null =>
  yMapToRecord<DownspoutRecord>(maps(model).downspouts.get(id))

function listOf<T>(m: Y.Map<Y.Map<unknown>>): T[] {
  const out: T[] = []
  m.forEach((v) => { const r = yMapToRecord<T>(v); if (r) out.push(r) })
  return out
}

// ---------- PLAN PERF round (2026-10-06): per-doc-version READ CACHE ----------
// The 2026-10-06 audit of the Santiago model measured every hover frame, every
// annotation family, every pick, every generate and every plan cut starting with a
// full `resolveAllElements` — none of them cached — and the ROOF BEARING WALLS
// change (roofBearingWalls.ts, 2026-10-05) made each ROOF resolve re-resolve every
// wall, so one pass cost roofs × walls (8 × 152 on Santiago). Element records and
// resolved elements are pure functions of the doc, so they are cached here per DOC
// VERSION: the cache drops on every transaction (Y.Doc 'beforeObserverCalls' — before
// any observer can read — and again on 'update'), and reads INSIDE an open
// transaction bypass it entirely (a command may write a param and re-read the element
// in the same transaction; the post-transact hooks do exactly that). Cached objects
// are shared between callers — nothing in the kernel, the geometry worker or the app
// mutates a resolved element or a listed record (verified 2026-10-06); the ARRAYS
// handed out are fresh copies so a caller's in-place sort can never reorder another's.
interface DocReadCache {
  ver: number
  elements: ElementRecord[] | null
  resolved: Map<string, ResolvedElement | null>
  resolvedAll: ResolvedElement[] | null
  /** CHEAP FIXES round: the sorted room list (listRooms), once per doc version */
  rooms: RoomRecord[] | null
  // INCREMENTAL RESOLVE (2026-11-03): the resolved elements of the previous versions that are
  // still valid — reused for every element an edit did not reach (see resolveClosure)
  prev: Map<string, ResolvedElement | null>
  /** element ids written, added or removed since the last closure was taken */
  dirtyEls: Set<string>
  /** a change outside the elements map (types, materials, levels, settings, rooms …): nothing reusable */
  dirtyAll: boolean
  closure: { ver: number; all: boolean; ids: Set<string> } | null
  // PHASED SETTINGS SETS (styleSets.ts): resolution depends on the viewed phase only when the
  // model has extra sets; the '' signature IS the two fields above, any other gets its own
  sigByPhase: Map<string, string>
  bySig: Map<string, { resolved: Map<string, ResolvedElement | null>; resolvedAll: ResolvedElement[] | null }>
}
const readCaches = new WeakMap<Y.Doc, DocReadCache>()
function readCache(model: ModelDoc): DocReadCache {
  const d = model.ydoc
  let c = readCaches.get(d)
  if (!c) {
    const created: DocReadCache = {
      ver: 0, elements: null, resolved: new Map(), resolvedAll: null, rooms: null, sigByPhase: new Map(), bySig: new Map(),
      prev: new Map(), dirtyEls: new Set(), dirtyAll: false, closure: null,
    }
    c = created
    readCaches.set(d, created)
    const drop = () => {
      created.ver++; created.elements = null; created.resolvedAll = null; created.rooms = null
      // INCREMENTAL RESOLVE: what was resolved at the version just ended stays reusable until
      // the next closure says which ids an edit reached
      for (const [k, v] of created.resolved) created.prev.set(k, v)
      created.resolved.clear()
      created.closure = null
      created.sigByPhase.clear(); created.bySig.clear()
    }
    d.on('beforeObserverCalls', drop)
    d.on('update', drop)
    // INCREMENTAL RESOLVE: which element ids a transaction touched (a nested write's path starts
    // with the element id; the map's own events carry added / removed ids), and whether anything
    // outside the elements map changed (then every resolve is suspect and the cache rebuilds in full)
    const all = maps(model)
    all.elements.observeDeep((events) => {
      for (const ev of events) {
        if (ev.target === all.elements) { ev.keys.forEach((_, k) => created.dirtyEls.add(k)) }
        else {
          const p = ev.path[0]
          if (typeof p === 'string') created.dirtyEls.add(p)
          else created.dirtyAll = true
        }
      }
    })
    for (const [name, t] of Object.entries(all)) {
      if (name === 'elements') continue
      ;(t as Y.Map<unknown>).observeDeep(() => { created.dirtyAll = true })
    }
  }
  return c
}
/** the settings sets (styleSets.ts), raw from the doc */
export function getStyleSets(model: ModelDoc): StyleSetsSettings {
  return normalizeStyleSets(maps(model).settings.get(STYLE_SETS_KEY))
}
/** CHANGES IN A PHASE: the phases any element records a change in (per doc version) */
const changePhaseCache = new WeakMap<Y.Doc, { ver: number; phases: Set<string> }>()
function changePhasesOf(model: ModelDoc): Set<string> {
  const ver = readCache(model).ver
  const hit = changePhaseCache.get(model.ydoc)
  if (hit && hit.ver === ver && !inTransaction(model)) return hit.phases
  const phases = new Set<string>()
  for (const el of listElements(model)) {
    for (const ch of normalizePhaseChanges((el.params as Record<string, unknown>)[PHASE_CHANGES_KEY])) phases.add(ch.phase)
    // DEMOLISH LAYERS: a wall with layers taken off is built peeled from its scope phase on, so
    // its solids differ across that phase like any change (the worker keys its caches on this)
    if (el.category === 'wall') {
      const sc = normalizeScope((el.params as Record<string, unknown>)[SCOPE_KEY])
      if (sc && (Object.values(sc.parts).some((p) => p.verb === 'remove') || (sc.add && Object.keys(sc.add).length))) phases.add(sc.phase)
    }
  }
  if (!inTransaction(model)) changePhaseCache.set(model.ydoc, { ver, phases })
  return phases
}
/** Everything a read at `phaseId` depends on beyond the doc itself: which Project Settings sets
 *  stand, and the latest phase with an element change at or before it. '' = nothing in the model
 *  depends on the viewed phase (no extra sets, no changes) — every cache keyed on it behaves
 *  exactly as before either existed. The geometry worker keys its solids cache on this too. */
export function resolveSignature(model: ModelDoc, phaseId: string | null | undefined): string {
  const phases = listPhases(model)
  const a = styleSetSignature(getStyleSets(model), phases, phaseId)
  const b = changePhaseKey(changePhasesOf(model), phases, phaseId)
  return a && b ? `${a}|${b}` : a || b
}

/** the read cache bucket for the ambient resolve phase's set signature */
function cacheBucket(model: ModelDoc, c: DocReadCache): { resolved: Map<string, ResolvedElement | null>; resolvedAll: ResolvedElement[] | null } {
  const phase = currentResolvePhase() ?? ''
  let sig = c.sigByPhase.get(phase)
  if (sig === undefined) {
    sig = resolveSignature(model, currentResolvePhase())
    c.sigByPhase.set(phase, sig)
  }
  if (sig === '') return c
  let b = c.bySig.get(sig)
  if (!b) { b = { resolved: new Map(), resolvedAll: null }; c.bySig.set(sig, b) }
  return b
}

/** true while a transaction is open on this doc (reads inside it see the live maps) */
function inTransaction(model: ModelDoc): boolean {
  return (model.ydoc as unknown as { _transaction?: unknown })._transaction != null
}
/** A monotonic version of the doc's CONTENT — bumps on every transaction. Consumers key
 *  their own derived caches on it (planPick's bodies, ModelerPage's hidden sets). */
export function docVersion(model: ModelDoc): number {
  return readCache(model).ver
}

export const listElements = (model: ModelDoc): ElementRecord[] => {
  if (inTransaction(model)) return listOf<ElementRecord>(maps(model).elements)
  const c = readCache(model)
  if (!c.elements) c.elements = listOf<ElementRecord>(maps(model).elements)
  return c.elements.slice()
}
export const listTypes = (model: ModelDoc) => listOf<TypeRecord>(maps(model).types)
export const listAssemblies = (model: ModelDoc) => listOf<AssemblyRecord>(maps(model).assemblies)
export const listMaterials = (model: ModelDoc) => listOf<MaterialRecord>(maps(model).materials)
export const listLevels = (model: ModelDoc): LevelRecord[] =>
  listOf<LevelRecord>(maps(model).levels).sort((a, b) => a.order - b.order)
export const listViews = (model: ModelDoc): ViewRecord[] =>
  listOf<ViewRecord>(maps(model).views).sort((a, b) => a.name.localeCompare(b.name))
// SET SCAFFOLDING (bim-plan/28 round 8): the set order is Cortex's Drawing Sort Order when the
// sheet carries it (G before A, discipline order); sheets without one keep their number order,
// after the keyed ones — so a model with no keyed sheets orders exactly as before
const sheetKey = (s: SheetRecord) => (s.sortKey ? `0|${s.sortKey}` : `1|${s.number}`)
export const listSheets = (model: ModelDoc): SheetRecord[] =>
  listOf<SheetRecord>(maps(model).sheets).sort((a, b) => sheetKey(a).localeCompare(sheetKey(b), undefined, { numeric: true }))
export const listDrafting = (model: ModelDoc) => listOf<DraftingRecord>(maps(model).drafting)
export const listAnnoTypes = (model: ModelDoc): AnnoTypeRecord[] =>
  listOf<AnnoTypeRecord>(maps(model).annoTypes).sort((a, b) =>
    a.kind === b.kind ? a.textIn - b.textIn || a.name.localeCompare(b.name) : a.kind.localeCompare(b.kind))
export const listGrids = (model: ModelDoc): GridRecord[] =>
  listOf<GridRecord>(maps(model).grids).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true }))
export const listPhases = (model: ModelDoc): PhaseRecord[] =>
  listOf<PhaseRecord>(maps(model).phases).sort((a, b) => a.order - b.order)
export const listTapeNotes = (model: ModelDoc): TapeNoteRecord[] =>
  listOf<TapeNoteRecord>(maps(model).tapeNotes).sort((a, b) => a.createdAt - b.createdAt)
// CHEAP FIXES round (2026-11-03): resolving a floor/ceiling reads the room list, so a
// full resolve re-read and re-sorted it once per element (a third of Santiago's 335 ms
// resolve after every edit). Cached per doc version like the elements; a read inside
// an open transaction still sees the live map.
export const listRooms = (model: ModelDoc): RoomRecord[] => {
  const build = () => listOf<RoomRecord>(maps(model).rooms).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
  if (inTransaction(model)) return build()
  const c = readCache(model)
  if (!c.rooms) c.rooms = build()
  return c.rooms.slice()
}
export const listRoomSeparators = (model: ModelDoc) =>
  listOf<RoomSeparatorRecord>(maps(model).roomSeparators)
export const getCircuit = (model: ModelDoc, id: CircuitId): CircuitRecord | null =>
  yMapToRecord<CircuitRecord>(maps(model).circuits.get(id))
/** circuits by name, numeric-aware ('Circuit 2' before 'Circuit 10') */
export const listCircuits = (model: ModelDoc): CircuitRecord[] =>
  listOf<CircuitRecord>(maps(model).circuits).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
export const listDraftLayers = (model: ModelDoc): DraftLayerRecord[] =>
  listOf<DraftLayerRecord>(maps(model).draftLayers).sort((a, b) => a.name.localeCompare(b.name))
/** property lines first (they anchor the parcel read), then easements, stable by name */
export const listSiteRecords = (model: ModelDoc): SiteRecord[] =>
  listOf<SiteRecord>(maps(model).site).sort((a, b) =>
    a.kind === b.kind ? (a.name ?? '').localeCompare(b.name ?? '') : a.kind === 'propertyLine' ? -1 : 1)
export const listDownspouts = (model: ModelDoc): DownspoutRecord[] =>
  listOf<DownspoutRecord>(maps(model).downspouts)

export function getMeta(model: ModelDoc): ModelMeta {
  const m = maps(model).meta
  return {
    schemaVersion: (m.get('schemaVersion') as number) ?? 1,
    conceptId: (m.get('conceptId') as string) ?? '',
    projectId: (m.get('projectId') as string) ?? '',
    units: 'ft',
  }
}

/** A gutter's finish — roofs and awnings share it: the picked material, else the Metals role's
 *  bare metal (copper / zinc / galvanized / stainless), else the firm's painted-aluminum gutter;
 *  the palette's Metals color (or a brushed one) over it. */
function gutterMaterialOf(model: ModelDoc, picked: string | null, color: unknown): string {
  const base = picked ?? metalsBareMaterial(model) ?? (listMaterials(model).find((x) => x.firmId === GUTTER_MATERIAL_FIRM_ID || x.name === GUTTER_MATERIAL_NAME)?.id ?? 'default')
  return tinted(base, metalsHex(model, base === 'default' ? null : base, color))
}

/** PHASED SETTINGS SETS: the eave set this element reads at the ambient resolve phase (its
 *  `styleSetPins.eaves` first) — null = the project's original eave values */
function eaveSetFor(model: ModelDoc, el: ElementRecord): StyleSet | null {
  const all = getStyleSets(model)
  if (!all.eaves?.length) return null
  return setForElement(all, 'eaves', (el.params as Record<string, unknown>).styleSetPins, listPhases(model), currentResolvePhase())
}
function eavesFor(model: ModelDoc, el: ElementRecord): EaveStyle {
  const set = eaveSetFor(model, el)
  return normalizeTrimStyle(set ? { eaves: set.values } : maps(model).settings.get('trimStyle')).eaves
}

/** Elements hosted into `hostId` (doors/windows in a wall — Phase 2 consumers). */
export function hostedElements(model: ModelDoc, hostId: ElementId): ElementRecord[] {
  return listElements(model).filter((e) => e.hostId === hostId)
}

// ---------- resolution ----------

/**
 * INCREMENTAL RESOLVE (2026-11-03): the ids whose resolve an edit can have changed. A resolved
 * element is a function of its own record, the shared tables (types, assemblies, materials,
 * levels, settings, rooms, phases …) and, per the inventory of resolveElementRecord's reads:
 *   · its HOST (niches, awnings, doors / windows, hanging lights, gates read the host they sit in)
 *   · for a ROOF, every wall (roofBearingWalls walks the model)
 *   · for CASEWORK, its run-mates and the walls (run geometry, splash tops, counter ends at walls)
 *   · for an AWNING, the opening it dresses and that opening's siblings on the wall
 * So: a table change → everything; else the written ids, plus roofs when a wall changed, casework
 * when a wall or a box changed, awnings when a wall or an opening changed, and — transitively —
 * whatever is hosted on any of those. A removed id has no category to reason from → everything.
 * Taken once per doc version; the ids it names are evicted from `prev` so a stale resolve can
 * never be handed out at a later version.
 */
function resolveClosure(model: ModelDoc, c: DocReadCache): { all: boolean; ids: Set<string> } {
  if (c.closure && c.closure.ver === c.ver) return c.closure
  let all = c.dirtyAll
  const ids = new Set<string>()
  if (!all) {
    const els = listElements(model)
    const byId = new Map(els.map((e) => [e.id as string, e]))
    const cats = new Set<string>()
    for (const id of c.dirtyEls) {
      const e = byId.get(id)
      if (!e) { all = true; break }
      cats.add(e.category)
      ids.add(id)
    }
    if (!all) {
      const wall = cats.has('wall')
      const openings = wall || cats.has('door') || cats.has('window') || cats.has('awning')
      const casework = wall || cats.has('casework')
      for (const e of els) {
        if ((e.category === 'roof' && wall) || (e.category === 'awning' && openings) || (e.category === 'casework' && casework)) ids.add(e.id as string)
      }
      let grew = true
      while (grew) {
        grew = false
        for (const e of els) if (e.hostId && ids.has(e.hostId as string) && !ids.has(e.id as string)) { ids.add(e.id as string); grew = true }
      }
    }
  }
  if (all) c.prev.clear()
  else for (const id of ids) c.prev.delete(id)
  c.dirtyEls.clear(); c.dirtyAll = false
  c.closure = { ver: c.ver, all, ids }
  return c.closure
}

export function resolveElement(model: ModelDoc, id: ElementId): ResolvedElement | null {
  if (inTransaction(model)) {
    const el = getElement(model, id)
    return el ? resolveElementRecord(model, el) : null
  }
  // PLAN PERF round: cached per doc version (see readCache above), and per standing-set
  // signature of the ambient resolve phase (styleSets.ts)
  const rc = readCache(model)
  const c = cacheBucket(model, rc)
  const hit = c.resolved.get(id)
  if (hit !== undefined) return hit
  // INCREMENTAL RESOLVE: in the plain bucket, an element the edit did not reach is the object
  // resolved before (the phase-signature buckets rebuild in full, as before)
  if (c === rc) {
    const cl = resolveClosure(model, rc)
    if (!cl.all && !cl.ids.has(id as string)) {
      const prev = rc.prev.get(id as string)
      if (prev !== undefined) { c.resolved.set(id, prev); return prev }
    }
  }
  const el = getElement(model, id)
  const r = el ? resolveElementRecord(model, el) : null
  c.resolved.set(id, r)
  return r
}

// ---------- CASEWORK STUDIO: run neighbours (2026-10-09, Matt: "the face frame between two
// boxes shows double width … in a face frame configuration we'd not see that seam") ----------
// Two face-frame boxes side by side in a run share ONE stile of the usual width: the box
// further along the run gives up its own stile on that side and the box before it extends
// its stile half a stile across the joint, so one piece covers it (no seam, no double
// width). A furniture base does the same bookkeeping for its end returns. The flags are
// folded into the TYPE params (__ffL/__ffR, __baseRun/__baseRetL/__baseRetR/__baseEndRet) so the
// prototype key forks on them.
const runOrderCaches = new WeakMap<Y.Doc, { ver: number; runs: Map<string, ElementRecord[]> }>()
/** BAYS AND LAYERS: a casework box's layer — 'upper' for an upper (its own override, else its
 *  type's), else 'floor' (base, tall, closet). Mirrors caseworkRun.ts#memberLayer (kept here to
 *  avoid an import cycle). */
function caseworkRowOf(model: ModelDoc, e: ElementRecord): number {
  const box = e.params.boxStyle as unknown as Record<string, unknown> | undefined
  const own = box && typeof box === 'object' && typeof box.style === 'string' ? box.style : null
  return rowOfParams(e.params as Record<string, unknown>, own || String(getType(model, e.typeId)?.params.style || 'base')).row
}
/** CASEWORK ROWS (bim-plan/36): the layer is the row — 'floor' (row 0), 'upper' (row 1), 'row<n>' */
function caseworkLayerOf(model: ModelDoc, e: ElementRecord): string {
  const r = caseworkRowOf(model, e)
  return r <= 0 ? 'floor' : r === 1 ? 'upper' : `row${r}`
}
/** the members of one layer of a run, in order — the run's PRIMARY layer (floor, unless it has
 *  only uppers) when `layer` is omitted */
function runLayerInOrder(model: ModelDoc, runId: string, layer?: string): ElementRecord[] {
  const all = runMembersInOrder(model, runId)
  // the PRIMARY layer: the lowest row the run has a box in
  const low = all.length ? Math.min(...all.map((e) => caseworkRowOf(model, e))) : 0
  const want = layer ?? (low <= 0 ? 'floor' : low === 1 ? 'upper' : `row${low}`)
  return all.filter((e) => caseworkLayerOf(model, e) === want)
}

function runMembersInOrder(model: ModelDoc, runId: string): ElementRecord[] {
  const live = inTransaction(model)
  const ver = live ? -1 : docVersion(model)
  let c = live ? undefined : runOrderCaches.get(model.ydoc)
  if (!c || c.ver !== ver) {
    const runs = new Map<string, ElementRecord[]>()
    for (const e of listElements(model)) {
      if (e.category !== 'casework' || typeof e.params.runId !== 'string' || !e.params.runId) continue
      const arr = runs.get(e.params.runId) ?? []
      arr.push(e)
      runs.set(e.params.runId, arr)
    }
    for (const arr of runs.values()) {
      const r = (((arr[0].params.rotationDeg as number) || 0) * Math.PI) / 180
      const s = (e: ElementRecord) => {
        const pt = (e.params.path as { nodes?: { pt: [number, number] }[] } | null)?.nodes?.[0]?.pt ?? [0, 0]
        return pt[0] * Math.cos(r) + pt[1] * Math.sin(r)
      }
      arr.sort((a, b) => s(a) - s(b))
    }
    c = { ver, runs }
    if (!live) runOrderCaches.set(model.ydoc, c)
  }
  return c.runs.get(runId) ?? []
}

// ---------- CASEWORK ROWS stage 2 (bim-plan/36): heights, bottoms and depths from rows ----------
// A run that has STORED row settings (`runRows`, written the first time a row is added or
// changed) gives each box its bottom, height and depth from its row; a run without them is left
// exactly as before. The box's own overrides always win (its height, its bottom, its depth).
export interface RunRowsGeom {
  rows: RunRowSetting[]
  bottoms: number[]
  tops: number[]
  /** every member's row, span and stretch along the run (from the run's start) */
  boxes: Map<string, { row: number; span: number; s0: number; s1: number }>
}
const runRowsCaches = new WeakMap<Y.Doc, { ver: number; map: Map<string, RunRowsGeom | null> }>()
export function runRowsGeom(model: ModelDoc, runId: string): RunRowsGeom | null {
  const live = inTransaction(model)
  const ver = live ? -1 : docVersion(model)
  let c = live ? undefined : runRowsCaches.get(model.ydoc)
  if (!c || c.ver !== ver) { c = { ver, map: new Map() }; if (!live) runRowsCaches.set(model.ydoc, c) }
  if (c.map.has(runId)) return c.map.get(runId)!
  const all = runMembersInOrder(model, runId)
  const stored = all.find((e) => Array.isArray(e.params.runRows))?.params.runRows as unknown as RunRowSetting[] | undefined
  let g: RunRowsGeom | null = null
  if (stored && stored.length) {
    const rows = stored.map((r) => ({ ...r }))
    // a counter-standing row sits on the run's countertop: its thickness (the run style's, else
    // the base type's)
    const baseRec = all.find((e) => String(getType(model, e.typeId)?.params.style || 'base') === 'base')
    const runCt = (baseRec?.params.runStyle as unknown as Record<string, unknown> | undefined)?.counterThicknessIn
    const typeCt = baseRec ? getType(model, baseRec.typeId)?.params.counterThicknessIn : undefined
    const ctIn = typeof runCt === 'number' ? runCt : typeof typeCt === 'number' ? typeCt : 1.5
    const { bottoms, tops } = rowLevels(rows, ctIn / 12)
    const r = (((all[0].params.rotationDeg as number) || 0) * Math.PI) / 180
    const sOf = (e: ElementRecord) => {
      const pt = (e.params.path as { nodes?: { pt: [number, number] }[] } | null)?.nodes?.[0]?.pt ?? [0, 0]
      return pt[0] * Math.cos(r) + pt[1] * Math.sin(r)
    }
    const boxes = new Map<string, { row: number; span: number; s0: number; s1: number }>()
    let start = Infinity
    for (const e of all) { const w = typeof e.params.widthFt === 'number' ? e.params.widthFt : 2; start = Math.min(start, sOf(e) - w / 2) }
    for (const e of all) {
      const box = e.params.boxStyle as unknown as Record<string, unknown> | undefined
      const own = box && typeof box === 'object' && typeof box.style === 'string' ? box.style : null
      const { row, span } = rowOfParams(e.params as Record<string, unknown>, own || String(getType(model, e.typeId)?.params.style || 'base'))
      const w = typeof e.params.widthFt === 'number' ? e.params.widthFt : 2
      const c0 = sOf(e) - start
      boxes.set(e.id, { row, span, s0: c0 - w / 2, s1: c0 + w / 2 })
    }
    g = { rows, bottoms, tops, boxes }
  }
  c.map.set(runId, g)
  return g
}

/** lay a box's row geometry over its resolved params (after the run style is folded) */
function applyRowGeometry(model: ModelDoc, el: ElementRecord, tp: Record<string, ParamValue>, params: Record<string, ParamValue>): void {
  const runId = el.params.runId
  if (typeof runId !== 'string' || !runId) return
  const g = runRowsGeom(model, runId)
  if (!g) return
  const me = g.boxes.get(el.id)
  if (!me) return
  const last = g.rows.length - 1
  const top = Math.min(me.row + me.span - 1, last)
  const own = styleMapOf(el.params.boxStyle)
  const ownH = typeof el.params.heightFt === 'number'
  if (me.row === 0) {
    // a tall box stands to the top of the highest row it reaches (Matt, Q1)
    if (me.span > 1 && !ownH && top > 0) params.heightFt = g.tops[top]
  } else if (me.row <= last) {
    if (typeof own.mountHeightFt !== 'number') tp.mountHeightFt = g.bottoms[me.row]
    if (!ownH) params.heightFt = g.tops[top] - g.bottoms[me.row]
    const rd = g.rows[me.row].depthFt
    if (typeof own.depthFt !== 'number' && typeof rd === 'number' && rd > 0) tp.depthFt = rd
    // a light rail hides under-cabinet lights: only under a row that hangs over a gap
    if (g.rows[me.row].bottom !== 'hung') tp.lightRail = 'none'
    // stage 3 (Q6): a counter-standing row with its base molding wears the furniture base board
    // (it continues across touching neighbours and returns at the ends, like a base's)
    tp.__rowBaseMolding = false
    if (g.rows[me.row].bottom === 'counter' && g.rows[me.row].baseMolding === true) {
      tp.__rowBaseMolding = true
      tp.baseStyle = 'furniture'
      tp.baseHeightIn = ROW_BASE_MOLDING_IN
    }
  }
  // a box with another stacked straight on top of it has no crown (the top one carries it)
  const above = g.rows[top + 1]
  if (above && above.bottom === 'stacked') {
    for (const [id, b] of g.boxes) {
      if (id === el.id || b.row !== top + 1) continue
      if (Math.min(b.s1, me.s1) - Math.max(b.s0, me.s0) > 1 / 12) { tp.crownStyle = 'none'; break }
    }
  }
}

// ---------- CASEWORK STUDIO stage 5: CORNERS (2026-10-09) ----------
// Two runs on one level, at right angles, where one run's END reaches the other's BACK LINE
// (its wall) and the other run faces back along it: that is a corner. The run whose end box
// fills the corner square OWNS it (its end box becomes the corner unit — blind, lazy susan,
// diagonal or dead); the other run butts it. Derived from the boxes' own geometry every time,
// cached per doc version — nothing about the pairing is stored, only the owner's chosen kind
// (`cornerStart` / `cornerEnd` in its run style).
/** a lazy susan / diagonal corner unit's leg along each wall (36") */
export const CORNER_LEG_FT = 3
/** UPPER CORNERS (2026-10-15): an L-shaped / diagonal UPPER corner's leg along each wall when
 *  the uppers are a run of their own (24", the wall-cabinet standard) */
export const UPPER_CORNER_LEG_FT = 2
/** the key `runCornersOf` uses for the UPPER layer over a base run (the run's own id is its
 *  floor line) */
export const upperLayerKey = (runId: string): string => rowKey(runId, 1)

export interface RunCorner {
  partnerRunId: string
  partnerEnd: 'start' | 'end'
  /** this run's end box fills the corner square */
  owner: boolean
  /** depth of this run's end box, and of the partner's near box (feet) */
  depthSelf: number
  depthPartner: number
  /** where the partner's near end stands, measured out from this run's back line (feet) */
  partnerAt: number
  /** OPEN CORNER (2026-10-15, Armando's model): neither run reaches the other's wall — each
   *  stops at the other's front and the corner square stands empty. Still a corner (the Studio
   *  offers its units, and picking one closes the square), but nothing turns it yet: no counter
   *  weld, the trim keeps its returns */
  open?: boolean
}
type RunInfo = { id: string; levelId: string; style: string; rot: number; dir: [number, number]; front: [number, number]; a: [number, number]; b: [number, number]; d0: number; d1: number }
const runCornerCaches = new WeakMap<Y.Doc, { ver: number; map: Map<string, { start?: RunCorner; end?: RunCorner }> }>()
export function runCornersOf(model: ModelDoc, runId: string): { start?: RunCorner; end?: RunCorner } {
  const live = inTransaction(model)
  const ver = live ? -1 : docVersion(model)
  let c = live ? undefined : runCornerCaches.get(model.ydoc)
  if (!c || c.ver !== ver) {
    c = { ver, map: computeRunCorners(model) }
    if (!live) runCornerCaches.set(model.ydoc, c)
  }
  return c.map.get(runId) ?? {}
}
function computeRunCorners(model: ModelDoc): Map<string, { start?: RunCorner; end?: RunCorner }> {
  const byRun = new Map<string, ElementRecord[]>()
  for (const e of listElements(model)) {
    if (e.category !== 'casework' || typeof e.params.runId !== 'string' || !e.params.runId) continue
    const arr = byRun.get(e.params.runId) ?? []
    arr.push(e)
    byRun.set(e.params.runId, arr)
  }
  const depthOf = (e: ElementRecord) => caseworkBoxDepthFt(e.params, getType(model, e.typeId)?.params)
  const runs: RunInfo[] = []
  // UPPER CORNERS (2026-10-15): a run's floor line AND its upper layer each turn corners — the
  // uppers over a base run pair with the uppers over the run round the corner (keyed
  // `upperLayerKey(runId)`), an upper-only run with either
  const layers: [string, ElementRecord[]][] = []
  for (const [id] of byRun) {
    const list = runLayerInOrder(model, id)
    if (!list.length) continue
    layers.push([id, list])
    if (caseworkLayerOf(model, list[0]) === 'floor') {
      // CASEWORK ROWS: row 1 turns corners (rows above it get theirs in bim-plan/36 stage 2)
      const ups = runLayerInOrder(model, id, 'upper')
      if (ups.length) layers.push([rowKey(id, 1), ups])
    }
  }
  for (const [id, list] of layers) {
    const rot = typeof list[0].params.rotationDeg === 'number' ? list[0].params.rotationDeg : 0
    const r = (rot * Math.PI) / 180
    const dir: [number, number] = [Math.cos(r), Math.sin(r)], front: [number, number] = [-Math.sin(r), Math.cos(r)]
    const ptOf = (e: ElementRecord) => (e.params.path as { nodes?: { pt: [number, number] }[] } | null)?.nodes?.[0]?.pt ?? [0, 0]
    const wOf = (e: ElementRecord) => (typeof e.params.widthFt === 'number' ? e.params.widthFt : 2)
    const p0 = ptOf(list[0]), p1 = ptOf(list[list.length - 1])
    runs.push({
      id, levelId: list[0].levelId, style: caseworkLayerOf(model, list[0]) === 'upper' ? 'upper' : String(getType(model, list[0].typeId)?.params.style ?? 'base'), rot, dir, front,
      a: [p0[0] - dir[0] * wOf(list[0]) / 2, p0[1] - dir[1] * wOf(list[0]) / 2],
      b: [p1[0] + dir[0] * wOf(list[list.length - 1]) / 2, p1[1] + dir[1] * wOf(list[list.length - 1]) / 2],
      d0: depthOf(list[0]), d1: depthOf(list[list.length - 1]),
    })
  }
  const out = new Map<string, { start?: RunCorner; end?: RunCorner }>()
  const slot = (id: string) => { let m = out.get(id); if (!m) { m = {}; out.set(id, m) } return m }
  const dot = (p: [number, number], q: [number, number]) => p[0] * q[0] + p[1] * q[1]
  const sub = (p: [number, number], q: [number, number]): [number, number] => [p[0] - q[0], p[1] - q[1]]
  const TOL = 0.25
  for (const A of runs) {
    for (const E of ['start', 'end'] as const) {
      const P = E === 'start' ? A.a : A.b
      const dSelf = E === 'start' ? A.d0 : A.d1
      const inward: [number, number] = E === 'start' ? A.dir : [-A.dir[0], -A.dir[1]]
      for (const B of runs) {
        // uppers turn corners with uppers, bases with bases (and talls)
        const fam = (s: string) => (s === 'upper' ? 'upper' : 'floor')
        if (B === A || B.levelId !== A.levelId || fam(B.style) !== fam(A.style)) continue
        let dd = Math.abs((B.rot - A.rot) % 360); if (dd > 180) dd = 360 - dd
        if (Math.abs(dd - 90) > 1) continue
        if (dot(B.front, inward) < 0.9) continue                        // B faces back along A
        const tA = dot(sub(B.a, P), A.front), tB = dot(sub(B.b, P), A.front)
        const nearEnd: 'start' | 'end' = tA <= tB ? 'start' : 'end'
        const tNear = Math.min(tA, tB), tFar = Math.max(tA, tB)
        const dB = nearEnd === 'start' ? B.d0 : B.d1
        const offWall = dot(sub(P, B.a), B.front)                       // A's end out from B's wall
        // A's end on B's wall — or an OPEN corner: A stops at B's front, B stops at A's front
        const onWall = Math.abs(offWall) <= TOL
        const open = !onWall && Math.abs(offWall - dB) <= TOL && Math.abs(tNear - dSelf) <= TOL
        if (!onWall && !open) continue
        if (tNear < -TOL || tNear > dSelf + 4 || tFar - tNear < 0.5) continue
        const mine = slot(A.id)
        if (mine[E]) continue
        const theirs = slot(B.id)
        // both reaching the corner (the boxes overlap there): the first run found owns it
        const theyOwn = theirs[nearEnd]?.owner === true
        mine[E] = { partnerRunId: B.id, partnerEnd: nearEnd, owner: !theyOwn, depthSelf: dSelf, depthPartner: dB, partnerAt: tNear, ...(open ? { open } : {}) }
        if (!theirs[nearEnd]) {
          theirs[nearEnd] = { partnerRunId: A.id, partnerEnd: E, owner: false, depthSelf: dB, depthPartner: dSelf, partnerAt: open ? offWall : 0, ...(open ? { open } : {}) }
        }
      }
    }
  }
  return out
}

/** STAGE 4: a full-height backsplash runs up to the underside of the uppers above it — the
 *  lowest upper (same level, same facing) whose box stands over this base's back, as a
 *  height above the level; null when nothing hangs there */
/**
 * CASEWORK ROWS stage 3: the stretches of a base box's width (box-local x, "x0,x1;x0,x1") that
 * a counter-standing box covers: the backsplash stops at its sides there.
 */
function caseworkSplashCuts(model: ModelDoc, el: ElementRecord, w: number): string {
  const runId = el.params.runId
  if (typeof runId !== 'string' || !runId) return ''
  const g = runRowsGeom(model, runId)
  const me = g?.boxes.get(el.id)
  if (!g || !me) return ''
  const cuts: string[] = []
  for (const [id, b] of g.boxes) {
    if (id === el.id || b.row < 1 || g.rows[b.row]?.bottom !== 'counter') continue
    // an open-wall spot builds nothing: the splash runs on behind it
    const o = getElement(model, id as ElementId)
    const cell = o ? (styleMapOf(o.params.boxStyle).upperCell ?? getType(model, o.typeId)?.params.upperCell) : null
    if (cell === 'open') continue
    const a = Math.max(b.s0, me.s0), z = Math.min(b.s1, me.s1)
    if (z - a < 1 / 48) continue
    const c = (me.s0 + me.s1) / 2
    cuts.push(`${(a - c).toFixed(5)},${(z - c).toFixed(5)}`)
  }
  void w
  return cuts.join(';')
}

function splashTopAbove(model: ModelDoc, el: ElementRecord, w: number): number | null {
  const pt = (el.params.path as { nodes?: { pt: [number, number] }[] } | null)?.nodes?.[0]?.pt
  if (!pt) return null
  const deg = (el.params.rotationDeg as number) || 0
  const r = (deg * Math.PI) / 180
  const u: [number, number] = [Math.cos(r), Math.sin(r)], n: [number, number] = [-Math.sin(r), Math.cos(r)]
  let best: number | null = null
  for (const o of listElements(model)) {
    if (o.category !== 'casework' || o.id === el.id || o.levelId !== el.levelId) continue
    const type = getType(model, o.typeId)
    if (!type || type.params.style !== 'upper') continue
    const op = (o.params.path as { nodes?: { pt: [number, number] }[] } | null)?.nodes?.[0]?.pt
    if (!op) continue
    let dd = Math.abs((((o.params.rotationDeg as number) || 0) - deg) % 360)
    if (dd > 180) dd = 360 - dd
    if (dd > 1) continue
    const dx = op[0] - pt[0], dy = op[1] - pt[1]
    if (Math.abs(dx * n[0] + dy * n[1]) > 0.6) continue
    const c = dx * u[0] + dy * u[1], ow = (o.params.widthFt as number) || 2
    if (Math.min(c + ow / 2, w / 2) - Math.max(c - ow / 2, -w / 2) < 1 / 12) continue
    // CASEWORK ROWS: only the first row above the counter stops the splash, and a row standing
    // ON the counter doesn't stop it (it cuts it instead: caseworkSplashCuts)
    const orow = caseworkRowOf(model, o)
    if (orow > 1) continue
    const ogr = typeof o.params.runId === 'string' ? runRowsGeom(model, o.params.runId) : null
    if (ogr && ogr.rows[orow]?.bottom === 'counter') continue
    // UPPER HEIGHT (2026-10-15): an upper's own bottom (its box override) wins
    const own = (o.params.boxStyle as unknown as Record<string, unknown> | undefined)?.mountHeightFt
    const og = typeof o.params.runId === 'string' ? runRowsGeom(model, o.params.runId) : null

    const mount = typeof own === 'number' ? own : og && og.rows.length > 1 ? og.bottoms[1] : typeof type.params.mountHeightFt === 'number' ? type.params.mountHeightFt : 4.5
    if (best === null || mount < best) best = mount

  }
  return best
}

/** COUNTER ENDS: is there a wall right at this end of the box (sign -1 = its local -x side)?
 *  Probed 1" past the end, 60% of the way to the front — clear of the wall behind the box */
function counterEndAtWall(model: ModelDoc, el: ElementRecord, resolved: ResolvedElement, sign: -1 | 1): boolean {
  const pt = (el.params.path as { nodes?: { pt: [number, number] }[] } | null)?.nodes?.[0]?.pt
  if (!pt) return false
  const w = (resolved.params.widthFt as number) || 2
  const d = (resolved.typeParams.depthFt as number) || 2
  const r = (((el.params.rotationDeg as number) || 0) * Math.PI) / 180
  const lx = sign * (w / 2 + 1 / 12), ly = d * 0.6
  const probe: [number, number] = [pt[0] + lx * Math.cos(r) - ly * Math.sin(r), pt[1] + lx * Math.sin(r) + ly * Math.cos(r)]
  for (const wl of listElements(model)) {
    if (wl.category !== 'wall' || wl.levelId !== el.levelId) continue
    const path = wl.params.path as unknown as import('./types').Path2D | undefined
    if (!path?.nodes?.length) continue
    if (projectCounterWallPt(flattenCounterWallPts(path), probe).dist <= 0.75) return true
  }
  return false
}

function applyCaseworkNeighbors(model: ModelDoc, el: ElementRecord, resolved: ResolvedElement, typeSchema: import('./params').ParamSchema) {
  const tp = resolved.typeParams
  // defaults = a box on its own: full stiles, its own base board, returns at both ends
  tp.__ffL = false; tp.__ffR = false
  for (const k of ['base', 'crown', 'rail']) { tp[`__${k}Run`] = 0; tp[`__${k}RetL`] = true; tp[`__${k}RetR`] = true; tp[`__${k}EndRet`] = true }
  // STAGE 4: a counter continues into the neighbour on that side (an interior joint — no end
  // overhang, no edge profile, no waterfall there)
  tp.__ctrL = false; tp.__ctrR = false
  // COUNTER ENDS (2026-10-23): an end against a wall stops flush (no overhang, no edge profile);
  // an end beside an appliance slot stops flush with its edge kept
  tp.__ctrWallL = false; tp.__ctrWallR = false; tp.__ctrGapL = false; tp.__ctrGapR = false
  tp.__splashTopFt = null
  // STAGE 4: an apron-front sink seated on a sink base replaces its false front — derived from
  // the sink actually standing on the box (its width, inches), never stored
  tp.__apronIn = 0
  if (tp.style === 'base' && tp.frontConfig === 'sink') {
    for (const o of listElements(model)) {
      if (o.category !== 'object' || o.hostId !== el.id) continue
      const ot = getType(model, o.typeId)
      if (ot?.params.objectKind !== 'kitchenSink') continue
      const st = plumbingStyleOf('kitchenSink', { ...ot.params, ...o.params })
      if (st?.counter === 'apron') { tp.__apronIn = plumbingWidthIn(st, { ...ot.params, ...o.params }); break }
    }
  }
  if (tp.style === 'base' && tp.counterSplash === 'full' && resolved.params.hasCounter !== false) {
    tp.__splashTopFt = splashTopAbove(model, el, (resolved.params.widthFt as number) || 2)
  }
  // CASEWORK ROWS stage 3: the backsplash stops at the sides of a box standing on the counter
  tp.__splashCuts = ''
  if (tp.style === 'base' && tp.counterSplash && tp.counterSplash !== 'none' && resolved.params.hasCounter !== false) {
    tp.__splashCuts = caseworkSplashCuts(model, el, (resolved.params.widthFt as number) || 2)
  }
  const runId = el.params.runId
  if (typeof runId !== 'string' || !runId) return
  // BAYS AND LAYERS: neighbours are boxes of the same layer that actually touch (an upper
  // either side of a tall box is no neighbour)
  const list = runLayerInOrder(model, runId, caseworkLayerOf(model, el))
  const i = list.findIndex((e) => e.id === el.id)
  if (i < 0) return
  type Info = { tp: Record<string, ParamValue>; h: number; part: string | null; w: number; ctr: boolean }
  const me: Info = {
    tp, h: (resolved.params.heightFt as number | null) ?? (tp.heightFt as number),
    part: typeof el.params.runPart === 'string' ? el.params.runPart : null, w: (resolved.params.widthFt as number) || 0,
    ctr: tp.style === 'base' && resolved.params.hasCounter !== false && tp.frontConfig !== 'slot',
  }
  const stationOf = (e: ElementRecord) => {
    const pt = (e.params.path as { nodes?: { pt: [number, number] }[] } | null)?.nodes?.[0]?.pt ?? [0, 0]
    const r = (((e.params.rotationDeg as number) || 0) * Math.PI) / 180
    const w = (e.params.widthFt as number) || 2
    const c = pt[0] * Math.cos(r) + pt[1] * Math.sin(r)
    return [c - w / 2, c + w / 2]
  }
  const info = (j: number): Info | null => {
    const nb = list[j]
    if (!nb) return null
    // only a box that touches the one before / after it along the chain counts
    const prev = list[j < i ? j + 1 : j - 1]
    if (prev) {
      const [a0, a1] = stationOf(prev), [b0, b1] = stationOf(nb)
      if (Math.min(Math.abs(a1 - b0), Math.abs(b1 - a0)) > 1e-3) return null
    }
    const type = getType(model, nb.typeId)
    if (!type) return null
    const ntp = resolveSchemaParams(typeSchema, type.params).params
    const np: Record<string, ParamValue> = { ...(nb.params as Record<string, ParamValue>) }
    foldCaseworkStyle(ntp, np)
    applyRowGeometry(model, nb, ntp, np)
    return {
      tp: ntp, h: (np.heightFt as number | null) ?? (ntp.heightFt as number), part: typeof nb.params.runPart === 'string' ? nb.params.runPart : null, w: (nb.params.widthFt as number) || 0,
      ctr: ntp.style === 'base' && np.hasCounter !== false && ntp.frontConfig !== 'slot',
    }
  }
  const shape = (x: Info) => `${x.tp.style}|${x.tp.style === 'upper' ? x.tp.mountHeightFt : 0}|${x.h}`
  const L = info(i - 1), R = info(i + 1)
  // a counter runs on into a neighbouring counter — and dies into a TALL box beside it like it
  // dies into a wall (no end overhang, profile or waterfall there; BAYS AND LAYERS)
  if (me.ctr) { tp.__ctrL = !!L?.ctr || L?.tp.style === 'tall'; tp.__ctrR = !!R?.ctr || R?.tp.style === 'tall' }
  if (me.ctr) {
    // local -x is the run's START side (seen from the room, on the right)
    for (const [side, n, key] of [['L', L, 'counterStart'], ['R', R, 'counterEnd']] as const) {
      if (tp['__ctr' + side]) continue
      if (n && n.tp.frontConfig === 'slot') { tp['__ctrGap' + side] = true; continue }
      const want = String(tp[key] || '')
      tp['__ctrWall' + side] = want === 'wall' || (want !== 'open' && !n && counterEndAtWall(model, el, resolved, side === 'L' ? -1 : 1))
    }
  }
  // face frames: boxes of one shape and one face-frame mode share the stile between them
  if (!me.part) {
    for (const [side, n] of [['L', L], ['R', R]] as const) {
      if (n && !n.part && shape(n) === shape(me) && hasFaceFrame(tp.frameMode) && n.tp.frameMode === tp.frameMode) tp['__ff' + side] = true
    }
  }
  // CONTINUOUS TRIM (stage 2): a furniture base / feet, a crown, a light rail each run as ONE
  // piece along a stretch of members that carry the same one — the FIRST member of the stretch
  // draws it for the whole stretch (no joint lines); an end that meets nothing gets a return;
  // a finished panel covers the end it meets. Flags per kind: __<k>Run (the rest of the
  // stretch's length, -1 = drawn by another), __<k>RetL/R, __<k>EndRet.
  const chain = (k: string, on: (x: Info) => boolean, sig: (x: Info) => string) => {
    tp[`__${k}Run`] = 0; tp[`__${k}RetL`] = true; tp[`__${k}RetR`] = true; tp[`__${k}EndRet`] = true
    const has = (x: Info | null) => !!x && x.part !== 'panel' && on(x)
    if (!has(me)) return
    const joined = (a2: Info | null, b2: Info | null) => has(a2) && has(b2) && sig(a2!) === sig(b2!)
      && (a2!.part === 'filler' || b2!.part === 'filler' || shape(a2!) === shape(b2!))
    const joinL = joined(L, me), joinR = joined(me, R)
    tp[`__${k}RetL`] = !joinL && L?.part !== 'panel'
    tp[`__${k}RetR`] = !joinR && R?.part !== 'panel'
    if (joinL) { tp[`__${k}Run`] = -1; return }
    let run = 0, j = i, cur: Info = me
    // FEET AT THE DIVISIONS (2026-10-09, Matt: "the internal feet should line up with the grids
    // so the feet are logically located between boxes"): where each joint along the stretch
    // falls, from this box's start — the feet stand there
    const joints: number[] = []
    for (;;) {
      const nx = info(j + 1)
      if (!joined(cur, nx)) { tp[`__${k}EndRet`] = nx?.part !== 'panel'; break }
      joints.push(me.w + run)
      run += nx!.w; cur = nx!; j++
    }
    tp[`__${k}Run`] = run
    if (k === 'base') tp.__baseJoints = joints.map((x) => x.toFixed(4)).join(',')
  }
  const styled = (x: Info, ...st: string[]) => x.part === 'filler' || st.includes(String(x.tp.style))
  chain('base', (x) => (x.tp.baseStyle === 'furniture' || x.tp.baseStyle === 'feet') && (styled(x, 'base', 'tall') || x.tp.__rowBaseMolding === true),
    (x) => `${x.tp.baseStyle}|${x.tp.baseProfile}|${x.tp.footStyle}|${x.tp.baseHeightIn}`)
  chain('crown', (x) => !!x.tp.crownStyle && x.tp.crownStyle !== 'none' && styled(x, 'upper', 'tall'),
    (x) => `${x.tp.crownStyle}|${x.tp.crownHeightIn}`)
  chain('rail', (x) => !!x.tp.lightRail && x.tp.lightRail !== 'none' && styled(x, 'upper'),
    (x) => String(x.tp.lightRail))
  // STAGE 5: a corner at this end of the run — the counter turns the corner (no end overhang,
  // profile or waterfall there), trim stops without a return, and the owning end box becomes
  // the corner unit the run style names
  // UPPER CORNERS (2026-10-15): the uppers over a base run turn their own corner (their kind is
  // the run style's cornerStartUpper / cornerEndUpper); an upper corner unit's leg reaches out
  // along the other wall to where the uppers round the corner begin
  const upperOfFloor = caseworkLayerOf(model, el) === 'upper' && runLayerInOrder(model, runId)[0]?.id !== list[0]?.id
  const corners = runCornersOf(model, upperOfFloor ? upperLayerKey(runId) : runId)
  const isUpper = tp.style === 'upper'
  tp.__corner = null; tp.__cornerSide = null; tp.__cornerDB = 0; tp.__cornerLeg = 0
  tp.__cornerWall = null; tp.__cornerReach = 0
  for (const [E, side, isEnd] of [['start', 'L', i === 0], ['end', 'R', i === list.length - 1]] as const) {
    const cn = corners[E]
    // an OPEN corner square turns nothing until a corner unit closes it
    if (!isEnd || !cn || cn.open) continue
    if (me.ctr) tp[`__ctr${side}`] = true

    for (const k of ['base', 'crown', 'rail']) tp[`__${k}Ret${side}`] = false
    // the owner's end stands against the other run's wall: its backsplash turns along it
    if (cn.owner && me.ctr) { tp.__cornerWall = side; tp.__cornerReach = cn.partnerAt }
    const kind = upperOfFloor ? (E === 'start' ? tp.cornerStartUpper : tp.cornerEndUpper) : (E === 'start' ? tp.cornerStart : tp.cornerEnd)
    if (cn.owner && typeof kind === 'string' && kind !== 'none' && !me.part) {
      tp.__corner = kind; tp.__cornerSide = side; tp.__cornerDB = cn.depthPartner
      tp.__cornerLeg = kind === 'lazySusan' || kind === 'diagonal' ? (isUpper ? cn.partnerAt : CORNER_LEG_FT) : cn.depthSelf
    }
  }
  delete tp.cornerStart; delete tp.cornerEnd; delete tp.cornerStartUpper; delete tp.cornerEndUpper

}

/** WALL PLACEMENT GHOST (2026-10-04): resolve an element record that ISN'T in the model
 *  (a placement preview) exactly the way a stored one resolves, so the ghost the user
 *  aims with is built from the same params the placed element will have */
export function resolveElementRecord(model: ModelDoc, elIn: ElementRecord): ResolvedElement | null {
  // CHANGES IN A PHASE (phaseChanges.ts): the element's params as of the ambient resolve phase —
  // every reader below sees a change exactly as if it were the element's own stored value
  const atPhase = (elIn.params as Record<string, unknown>)?.[PHASE_CHANGES_KEY]
    ? paramsAtPhase(elIn.params as Record<string, ParamValue>, listPhases(model), currentResolvePhase())
    : elIn.params
  const el: ElementRecord = atPhase === elIn.params ? elIn : { ...elIn, params: atPhase as never }
  const type = getType(model, el.typeId)
  if (!type) return null
  const cat = getCategory(el.category)
  if (!cat) return null
  const assembly = type.assemblyId ? getAssembly(model, type.assemblyId) : null
  const level = getLevel(model, el.levelId)
  const resolved = mergeAndResolve(el, type, assembly, level, cat.instanceParams, cat.typeParams)
  // CASEWORK STUDIO (2026-10-08): the run's style and this box's overrides, laid over the
  // type — before anything below (the cabinet color) reads them
  if (el.category === 'casework') {
    foldCaseworkStyle(resolved.typeParams, resolved.params)
    applyRowGeometry(model, el, resolved.typeParams, resolved.params)
    applyCaseworkNeighbors(model, el, resolved, cat.typeParams)
  }
  // STRUCTURE round: materialize level REFERENCES (param kind 'level' — a column's
  // topLevelId) into records, so consumers read elevations without a model handle
  for (const d of schemaParams(cat.instanceParams)) {
    if (d.kind === 'level') {
      const v = resolved.params[d.key]
      if (typeof v !== 'string' || !v) continue
      const lv = getLevel(model, v as LevelId)
      if (lv) (resolved.paramLevels ??= {})[d.key] = lv
    } else if (d.kind === 'typeRef') {
      // FOOTINGS round: materialize TYPE references (a wall's footingTypeId/
      // stemTypeId) — record + params resolved through the referenced category's
      // own typeParams schema, so consumers read real defaults without a model handle
      const v = resolved.params[d.key]
      if (typeof v !== 'string' || !v) continue
      const t = getType(model, v as TypeId)
      if (!t) continue   // dangling reference — consumers fall back to defaults
      const refCat = getCategory(t.category)
      const refParams = refCat ? resolveSchemaParams(refCat.typeParams, t.params).params : { ...t.params }
      // STEM FINISH LAYERS round (2026-08-17): resolve the REFERENCED type's own
      // assembly too (e.g. a wall's stemTypeId pointing at a layered stem type) —
      // same no-model-handle rationale as `refParams` above, so bim-geometry can
      // read a referenced type's layers without ever seeing `model`.
      const refAssembly = t.assemblyId ? getAssembly(model, t.assemblyId) : null
      ;(resolved.paramTypes ??= {})[d.key] = { record: t, params: refParams, assembly: refAssembly }
    }
  }
  // BRICK MODULES (2026-10-04): the brick size of a door/window's picked trim + sill
  // materials (blank = the wall's finish, resolved on the wall below)
  if (el.category === 'window' || el.category === 'door') {
    const brickOf = (v: unknown): string | null => {
      if (typeof v !== 'string' || !v) return null
      const c = claddingSpecOf(getMaterial(model, v as MaterialId))
      return c?.profile === 'masonry' ? (c.masonry?.unit || DEFAULT_MASONRY_UNIT) : null
    }
    const trim = brickOf(resolved.params.trimMaterial ?? resolved.typeParams.trimMaterial)
    const sill = brickOf(resolved.params.sillMaterial ?? resolved.typeParams.sillMaterial)
    if (trim || sill) resolved.trimBrick = { trim, sill }
  }
  // WALL NICHE (2026-10-07): the niche's built shape reads its host wall's assembly
  // (depth limit, the finish that wraps in) — resolved here, the generator has no model
  if (el.category === 'niche') {
    const host = el.hostId ? resolveElement(model, el.hostId as ElementId) : null
    resolved.nicheSpec = nicheSpec(resolved, host && host.category === 'wall' ? host : null)
    // head mode: every consumer reads the effective clear sill (the window precedent)
    resolved.params.sillFt = resolved.nicheSpec.sillFt
  }
  // AWNINGS (2026-10-08): the layout (its span, height, brackets, pitch — read off the opening
  // it dresses) and its finishes, resolved here (the generator has no model). Painted wood
  // wears the palette's Trim color, metal the Metals color, and the default roofing is the
  // firm's standing seam in the Metals color (copper on a copper project).
  if (el.category === 'awning') {
    const host0 = el.hostId ? resolveElement(model, el.hostId as ElementId) : null
    const host = host0 && host0.category === 'wall' ? host0 : null
    const oid = typeof resolved.params.openingId === 'string' ? resolved.params.openingId : ''
    const op0 = oid ? resolveElement(model, oid as ElementId) : null
    const opening = op0 && (op0.category === 'door' || op0.category === 'window') && op0.hostId === el.hostId ? op0 : null
    const tp = resolved.typeParams
    const byName = (n: string) => listMaterials(model).find((m) => m.name === n)?.id ?? null
    const pickedRoof = typeof tp.roofing === 'string' && tp.roofing && getMaterial(model, tp.roofing as MaterialId) ? tp.roofing : null
    const roofMat = pickedRoof ?? byName('Standing Seam Metal Roofing') ?? 'default'
    const rm = roofMat !== 'default' ? getMaterial(model, roofMat as MaterialId) : null
    const roofingSpec = rm ? roofingSpecOf(rm) : null
    const shStyle = (roofingSpec?.shingle as { style?: string } | undefined)?.style ?? null
    // the opening's mulled line (its neighbours on the wall) and the PROJECT's gutter — the same
    // standard every roof's eaves take (Project Settings → Trim & Eaves)
    const siblings = opening
      ? hostedElements(model, opening.hostId as ElementId)
        .filter((r) => r.id !== opening.id && (r.category === 'door' || r.category === 'window'))
        .map((r) => resolveElement(model, r.id)).filter((r): r is ResolvedElement => !!r)
      : []
    const eaves = eavesFor(model, el)
    const spec = awningSpec(resolved, host, opening, {
      profile: roofingSpec?.profile ?? 'flat', shingleStyle: shStyle, name: rm?.name, fireBanned: shStyle === 'shake',
    }, { siblings, gutterProfile: eaves.gutterProfile, gutterSize: eaves.gutterSize })
    resolved.awningSpec = spec
    const pick = (v: unknown) => (typeof v === 'string' && v && getMaterial(model, v as MaterialId) ? v : null)
    const frameBase = pick(tp.frameMaterial) ?? byName('Wood Rafter') ?? 'default'
    let fh = surfaceHex(model, frameBase, resolved.params.frameColor, spec.stained ? null : 'trim')
    if (!fh && spec.bracket === 'timber') fh = '#3b3029'   // heavy timber: stained dark
    const metalBase = pick(tp.metalMaterial) ?? metalsBareMaterial(model)
      ?? (listMaterials(model).find((x) => x.firmId === GUTTER_MATERIAL_FIRM_ID || x.name === GUTTER_MATERIAL_NAME)?.id ?? 'default')
    const mh = metalsHex(model, metalBase === 'default' ? null : metalBase, resolved.params.metalColor)
    const rh = pickedRoof
      ? surfaceHex(model, roofMat, resolved.params.roofingColor, null)
      : metalsHex(model, roofMat === 'default' ? null : roofMat, resolved.params.roofingColor)
    const fabric = byName(AWNING_FABRIC_MATERIAL_NAME) ?? 'default'
    resolved.awningMats = {
      frame: tinted(frameBase, fh), soffit: tinted(frameBase, fh), metal: tinted(metalBase, mh),
      roofing: roofMat, roofingSpec, roofingHex: rh,
      fabricA: tinted(fabric, spec.stripeA), fabricB: tinted(fabric, spec.stripeB),
      // the roofs' own gutter finish (a roof with no gutter material of its own)
      gutter: gutterMaterialOf(model, null, resolved.params.gutterColor),
    }
  }
  // CUPOLAS (2026-10-09): the finishes, resolved here (the generator has no model). The body
  // and trim wear the palette's Trim color, the metal (cladding, finial, vane) the Metals color,
  // and the default roofing is the firm's standing seam in the Metals color (the awning rule).
  // Cedar-shingle cladding is the firm's cedar shake, stained; board-and-batten the body.
  if (el.category === 'cupola') {
    const tp = resolved.typeParams
    const byName = (n: string) => listMaterials(model).find((m) => m.name === n)?.id ?? null
    const pick = (v: unknown) => (typeof v === 'string' && v && getMaterial(model, v as MaterialId) ? v : null)
    const pickedRoof = pick(tp.roofing)
    const roofMat = pickedRoof ?? byName('Standing Seam Metal Roofing') ?? 'default'
    const rm = roofMat !== 'default' ? getMaterial(model, roofMat as MaterialId) : null
    const roofingSpec = rm ? roofingSpecOf(rm) : null
    const rh = pickedRoof
      ? surfaceHex(model, roofMat, resolved.params.roofingColor, null)
      : metalsHex(model, roofMat === 'default' ? null : roofMat, resolved.params.roofingColor)
    const bodyBase = pick(tp.bodyMaterial) ?? byName('Wood Rafter') ?? 'default'
    const bh = surfaceHex(model, bodyBase, resolved.params.bodyColor, 'trim')
    const metalBase = pick(tp.metalMaterial) ?? metalsBareMaterial(model)
      ?? (listMaterials(model).find((x) => x.firmId === GUTTER_MATERIAL_FIRM_ID || x.name === GUTTER_MATERIAL_NAME)?.id ?? 'default')
    const mh = metalsHex(model, metalBase === 'default' ? null : metalBase, resolved.params.metalColor)
    const metal = tinted(metalBase, mh)
    const clad = tp.cladding === 'shingle' ? tinted(byName('Cedar Shake Roofing') ?? bodyBase, '#8f6a48')
      : tp.cladding === 'metal' ? metal : tinted(bodyBase, bh)
    resolved.cupolaMats = {
      body: tinted(bodyBase, bh), clad, metal, roofing: roofMat, roofingSpec, roofingHex: rh,
      glass: '__railGlass', shaft: byName('Gypsum Board') ?? tinted(bodyBase, '#f1efe9'), framing: byName('Wood Rafter') ?? 'default',
    }
  }
  // SHUTTERS: a window's (or a door's — 2026-10-30) shutter + hardware materials, resolved (the generator has no model)
  if ((el.category === 'window' || el.category === 'door') && typeof resolved.params.shutterStyle === 'string' && resolved.params.shutterStyle) {
    const byName = (n: string) => listMaterials(model).find((m) => m.name === n)?.id ?? null
    const picked = resolved.params.shutterMaterial
    const leaf = (typeof picked === 'string' && picked && getMaterial(model, picked as MaterialId) ? picked : null) ?? byName(SHUTTER_MATERIAL_NAMES.leaf)
    // COLORS (2026-10-06): the shutters show their brushed color, else the project standard
    // for the shutter finish (the leaf id is tinted here, so the generator needs nothing)
    const leafId = (leaf as string | null) ?? 'default'
    resolved.shutterMats = {
      leaf: tinted(leafId, surfaceHex(model, leafId, resolved.params.shutterColor)),
      hardware: byName(SHUTTER_MATERIAL_NAMES.hardware) ?? 'default',
    }
  }
  // TRIM & EAVES (2026-10-07): the project's eave defaults for every field this roof hasn't set
  if (el.category === 'roof') {
    const ts = normalizeTrimStyle(maps(model).settings.get('trimStyle'))
    // PHASED SETTINGS SETS: the eave set standing in the viewed phase (or the roof's pin)
    const eaveSet = eaveSetFor(model, el)
    if (eaveSet) resolved.styleSetIds = { ...(resolved.styleSetIds || {}), eaves: eaveSet.id }
    if (applyRoofEaveStyle(resolved.params, el.params as Record<string, ParamValue>, eaveSet ? normalizeTrimStyle({ eaves: eaveSet.values }).eaves : ts.eaves)) {
      resolved.styleFollows = { ...(resolved.styleFollows || {}), eaves: true } as never
    }
    // ROOFS & EAVES (2026-10-08): the project's standard pitch / shed pitch / overhang
    // a shed = exactly one sloped (non-gable) edge — commands/roof.ts#shedBearingEdgeIndex's rule
    const bnodes = (resolved.params.boundary as { nodes?: { edge?: { gable?: boolean } }[] } | null)?.nodes ?? []
    const isShed = bnodes.length >= 3 && bnodes.filter((n) => !n.edge?.gable).length === 1
    if (applyRoofStandards(resolved.params, el.params as Record<string, ParamValue>, isShed, ts)) {
      resolved.styleFollows = { ...(resolved.styleFollows || {}), roofStandards: true } as never
    }
    // CRICKET (2026-10-19, Matt): drawn like a roof, but it stops at its bearing line — no
    // overhang anywhere and no eave or rake dressing, whatever the project eave style or an
    // edge says. Resolved here, after the project defaults, so every consumer (body, framing,
    // tails, fascia, gutters, soffit, downspouts, details, grips) reads a bare roof. Stored
    // per-edge attrs are only ignored, never erased — switching it off brings them back.
    if (resolved.params.cricket === true) {
      const p = resolved.params
      p.overhangFt = 0
      p.tailsDefault = false; p.fasciaDefault = false; p.gutterDefault = false
      p.bargeDefault = false; p.friezeDefault = false
      p.eaveMode = 'boxed'; p.soffitStyle = 'sloped'; p.eaveStructureDepthIn = ''
      const b = p.boundary as { nodes?: { edge?: Record<string, unknown> }[] } | null
      if (b?.nodes) {
        p.boundary = {
          ...b,
          nodes: b.nodes.map((n) => {
            if (!n.edge) return n
            const { overhangFt: _o, tails: _t, fascia: _f, gutter: _g, barge: _b, flatSoffit: _s, frieze: _z, eaveStructureDepthIn: _d, ...rest } = n.edge
            return { ...n, edge: rest }
          }),
        } as never
      }
      if (resolved.styleFollows) resolved.styleFollows = { ...resolved.styleFollows, eaves: false } as never
    }
  }
  // ROOF BEARING WALLS (2026-10-05): each boundary edge learns how far the wall under it
  // reaches past the bearing line, so the overhang counts from the wall's outside face of
  // STUD (2026-10-12 — finishes never push the roof out); the finished face rides along
  // for the show members that sit against the wall
  // (a CRICKET bears on the roof under it, never on a wall — its edges stop at the bearing line)
  if (el.category === 'roof' && resolved.params.cricket !== true) {
    const bnd = resolved.params.boundary as { nodes?: { pt: [number, number]; edge?: { bearingWall?: unknown } }[] } | null
    if (bnd?.nodes && bnd.nodes.length >= 3 && resolved.level) {
      const zPlate = resolved.level.elevationFt + ((resolved.params.plateHeightFt as number) || 0)
      // BEARS ON (2026-10-13): each edge's own say — blank = auto, 'none', or a picked wall id
      const choices = bnd.nodes.map((n) => (typeof n.edge?.bearingWall === 'string' ? n.edge.bearingWall : null))
      const { ft, faceFt, ids, walls, modes } = roofBearingWalls(model, bnd.nodes.map((n) => [n.pt[0], n.pt[1]] as BearingPt2), zPlate, el.id, choices)
      if (ft.some((v) => v > 0)) { resolved.roofBearingWallFt = ft; resolved.roofBearingWallFaceFt = faceFt }
      resolved.roofBearingWallEdges = walls
      resolved.roofBearingWallModes = modes
      // PLAN PERF round: which walls — the worker's dirty closure re-derives this roof
      // when one of them moves (see docMirror#expandDirtyClosure)
      if (ids.length) resolved.roofBearingWallIds = ids
    }
  }
  // FOOTINGS follow-up: walls carry the PROJECT structural standards (coverage depth)
  // so the foundation generator never needs a model handle — the paramLevels pattern
  // applied to a settings-map read (normalizeStructural supplies defaults when unset)
  if (el.category === 'wall') {
    resolved.structural = normalizeStructural(maps(model).settings.get('structural'))
    // FACADE LINES: a wall's copies of lines drawn across several walls take their height
    // and paint from the line's own record (facadeLines.ts) — before anything reads bands
    if (Array.isArray(resolved.params.faceSplits) && (resolved.params.faceSplits as FaceSplitEntry[]).some((s) => s && s.lineId)) {
      const next = resolveFacadeSplits(model, el.id, resolved.params.faceSplits as FaceSplitEntry[], wallZSpan(resolved))
      resolved.params = { ...resolved.params, faceSplits: next.length ? next : null }
    }
    // SHEATHING OFF: the type's exterior sheathing left out of this wall (before any painted
    // finish goes on, so a finish sits straight on the studs)
    if (resolved.assembly && resolved.params.sheathing === false) {
      const bare = withoutSheathing(resolved.assembly)
      if (bare) { resolved.assembly = bare; resolved.assemblyThicknessFt = assemblyThickness(bare) }
    }
    // PAINTED WALL FINISHES: a painted face is a real layer of the resolved assembly
    const fx = resolved.params.finishExterior, fi = resolved.params.finishInterior
    if (resolved.assembly && ((typeof fx === 'string' && fx) || (typeof fi === 'string' && fi))) {
      const { assembly: painted, specs } = applyWallFinishes(
        resolved.assembly,
        { exterior: typeof fx === 'string' ? fx : null, interior: typeof fi === 'string' ? fi : null },
        (id) => getMaterial(model, id as MaterialId),
        (name) => listMaterials(model).find((m) => m.name === name) || null,
      )
      if (painted !== resolved.assembly) {
        resolved.assembly = painted
        resolved.assemblyThicknessFt = assemblyThickness(painted)
        resolved.claddingSpecs = withSidingSettings(model, specs)
      }
    }
    // DEMOLISH LAYERS round 2: the wall as its scope leaves it (layers off, new finishes on)
    if (resolved.assembly && resolved.params[SCOPE_KEY]) {
      const after = wallScopeAfter(resolved,
        (id) => getMaterial(model, id as MaterialId),
        (name) => listMaterials(model).find((m) => m.name === name) || null)
      if (after) resolved.scopeAfter = { ...after, claddingSpecs: withSidingSettings(model, after.claddingSpecs) }
    }
    // SPLIT-BAND THICKNESS: a band painted a finish of another thickness steps the face
    {
      const asm = resolved.assembly as import('./wallCladding').PaintedAssembly | null
      if (asm) {
        const steps = wallBandSteps(asm.paintedFrom || asm, resolved.assemblyThicknessFt, resolved.params,
          (id) => getMaterial(model, id as MaterialId),
          (name) => listMaterials(model).find((m) => m.name === name) || null)
        if (steps) resolved.faceBandSteps = steps
      }
      // a band painted a finish with modeled detail (vertical joints, battens) carries
      // its spec too, so the generator dresses the band, not just the face's own finish
      const bandSpecs: Record<string, import('./types').CladdingSpec> = {}
      for (const side of ['exterior', 'interior'] as const) {
        for (const b of faceBands(resolved.params, side)) {
          if (!b.materialId || bandSpecs[b.materialId]) continue
          const spec = claddingSpecOf(getMaterial(model, b.materialId as MaterialId))
          if (spec) bandSpecs[b.materialId] = spec.profile === 'lap' ? { ...spec, ...lapCornerOf(model) } : spec
        }
      }
      if (Object.keys(bandSpecs).length) resolved.bandCladdingSpecs = bandSpecs
      // WALL BANDS: each band's material resolved (a profile's default by name)
      const sweeps = faceSweepsOf(resolved.params)
      if (sweeps.length) {
        // r3 (Matt: brick bands "need to match the style of their hosted finishes"): a band
        // with no material of its own is laid in the face's own brick (a brick band) or stone
        // (a stone band) — the finish just above it, else just below — before the profile's
        // generic default
        const faceLayers = resolved.assembly?.layers ?? []
        const faceMatAt = (side: 'exterior' | 'interior', z: number): string | null => {
          const b = faceBands(resolved.params, side).find((x) => z >= x.z0 && z < x.z1)
          if (b?.materialId) return b.materialId
          const l = side === 'exterior' ? faceLayers[0] : faceLayers[faceLayers.length - 1]
          return l && l.function === 'finish' ? (l.materialId as string) : null
        }
        const kindOf = (m: string | null): 'brick' | 'stone' | null => {
          const mr = m ? getMaterial(model, m as MaterialId) : null
          const c = mr ? claddingSpecOf(mr) : null
          return c?.profile !== 'masonry' ? null : mr?.cladding?.masonry?.pattern ? 'stone' : 'brick'
        }
        resolved.faceSweeps = sweeps.map((s) => {
          const def = sweepProfileOf(s.profile).defaultMaterial
          const want = s.profile === 'rowlock' || s.profile === 'soldier' ? 'brick' : 'stone'
          const hosted = [faceMatAt(s.side, s.at + 1e-3), faceMatAt(s.side, s.at - 1e-3)].find((m) => kindOf(m) === want) ?? null
          const mat = (s.materialId && getMaterial(model, s.materialId as MaterialId) ? s.materialId : null)
            ?? hosted ?? listMaterials(model).find((m) => m.name === def)?.id ?? null
          // BRICK MODULES: a band in a brick material is laid in that brick's size
          const c = mat ? claddingSpecOf(getMaterial(model, mat as MaterialId)) : null
          const brickUnit = c?.profile === 'masonry' ? (c.masonry?.unit || DEFAULT_MASONRY_UNIT) : null
          return { ...s, mat: mat as string | null, brickUnit }
        })
      }
    }
    // WALL COPINGS (2026-10-06): the coping's materials (the generator has no model) — brick
    // copings default to the wall's own brick (its exterior finish when that is brick)
    {
      const style = resolveCopingStyle({ ...resolved.typeParams, ...resolved.params })
      if (style !== 'none') {
        const byName = (n: string) => listMaterials(model).find((m) => m.name === n)?.id ?? null
        const picked = resolved.params.copingMaterial
        const own = typeof picked === 'string' && picked && getMaterial(model, picked as MaterialId) ? picked : null
        // r2 (Matt: "the brick module size should match the applied adjacent wall finish"):
        // the brick on MOST of the wall's faces — exterior, interior, ends — so a wall with the
        // long thin brick on two faces and a plain brick on one caps in the long thin
        const layers = resolved.assembly?.layers ?? []
        const faces = [layers[0], layers[layers.length - 1]].filter((l) => l && l.function === 'finish').map((l) => l!.materialId as string)
        if (typeof resolved.params.finishEnd === 'string') faces.push(resolved.params.finishEnd)
        const tally = new Map<string, number>()
        for (const m of faces) {
          if (claddingSpecOf(getMaterial(model, m as MaterialId))?.profile !== 'masonry' || getMaterial(model, m as MaterialId)?.cladding?.masonry?.pattern) continue
          tally.set(m, (tally.get(m) ?? 0) + 1)
        }
        let wallBrick: string | null = null
        for (const [m, c] of tally) if (!wallBrick || c > tally.get(wallBrick)!) wallBrick = m
        const main = own ?? (style === 'metal' ? byName(COPING_MATERIAL_NAMES.metal)
          : style === 'stoneSaddle' ? byName(COPING_MATERIAL_NAMES.stone)
          : wallBrick ?? byName(COPING_MATERIAL_NAMES.brick))
        const mc = main ? claddingSpecOf(getMaterial(model, main as MaterialId)) : null
        resolved.copingMats = {
          main: (main as string | null) ?? 'default',
          tile: byName(COPING_MATERIAL_NAMES.tile) ?? 'default',
          brick: mc?.profile === 'masonry' ? (mc.masonry?.unit || DEFAULT_MASONRY_UNIT) : null,
        }
      }
    }
    // BRICK MODULES (2026-10-04): the brick size of the wall's exterior finish (the outermost
    // finish layer — painted or the type's own), for brick trims laid in "the wall's brick"
    {
      const l0 = resolved.assembly?.layers?.[0]
      const c = l0 && l0.function === 'finish' ? claddingSpecOf(getMaterial(model, l0.materialId as MaterialId)) : null
      if (c?.profile === 'masonry') resolved.exteriorFinishBrick = c.masonry?.unit || DEFAULT_MASONRY_UNIT
    }
    // FINISH-FREE TYPES (finishStripMigration.ts): a wall moved off a finished type
    // carries the centerline correction that keeps its studs exactly where they were
    const anchor = resolved.params.finishAnchorFt
    if (typeof anchor === 'number' && anchor !== 0 && resolved.assembly) {
      const a = resolved.assembly as import('./wallCladding').PaintedAssembly
      resolved.assembly = (a.paintedFrom
        ? { ...a, paintShiftFt: (a.paintShiftFt || 0) + anchor }
        : { ...a, paintedFrom: a, paintShiftFt: anchor }) as import('./types').AssemblyRecord
    }
  }
  // PAINTED FLOOR/CEILING FINISHES: each painted area's finish thickness
  if (el.category === 'floor' || el.category === 'ceiling') {
    const areas = Array.isArray(resolved.params.finishAreas) ? (resolved.params.finishAreas as { materialId: string }[]) : []
    // PROJECT FINISH DEFAULTS (finishDefaults.ts): the rooms on this floor's level, each with the
    // flooring it takes when nobody has painted it — their materials join the painted ones below
    const defaultMats: string[] = []
    if (el.category === 'floor' && resolved.levelId) {
      const fd = getFinishDefaults(model)
      const usable = (id: string | null | undefined) => {
        const m = id ? getMaterial(model, id as MaterialId) : null
        return m?.floorFinish && m.floorFinish.thicknessFt > 0 ? (id as string) : null
      }
      const dry = usable(fd.floor), wet = usable(fd.wetFloor) ?? dry
      if (dry || wet) {
        const rooms = listRooms(model).filter((r) => r.levelId === resolved.levelId)
        const list = rooms.map((r) => {
          const kind = roomFloorKind(r.template)
          return { seedPt: [r.seedPt[0], r.seedPt[1]] as [number, number], materialId: kind === 'wet' ? wet : kind === 'dry' ? dry : null, ...(kind === 'wet' ? { wet: true } : {}) }
        }).filter((r) => r.materialId)
        if (list.length) {
          resolved.finishDefaultRooms = list
          resolved.finishDefaultDoor = fd.doorTransition || 'center'
          if (fd.threshold && fd.threshold !== 'none' && wet && wet !== dry) resolved.finishDefaultThreshold = { kind: fd.threshold }
          const seps = listRoomSeparators(model).filter((s) => s.levelId === resolved.levelId)
          resolved.finishDefaultSeparators = seps.map((s) => ((s as { pts?: [number, number][] }).pts ?? [s.a, s.b]) as [number, number][])
          for (const r of list) if (r.materialId && !defaultMats.includes(r.materialId)) defaultMats.push(r.materialId)
        }
      }
    }
    // ROOM FINISHES (roomFinishes.ts): a ceiling under a room with a ceiling finish wears it where
    // nobody painted the ceiling (the floor defaults' own mechanism); a floor carries its level's
    // rooms' wall zones for the geometry pass
    if (el.category === 'ceiling' && resolved.levelId) {
      const list = listRooms(model).filter((r) => r.levelId === resolved.levelId && r.finishes?.ceiling)
        .map((r) => ({ seedPt: [r.seedPt[0], r.seedPt[1]] as [number, number], materialId: r.finishes!.ceiling as string }))
        .filter((r) => getMaterial(model, r.materialId as MaterialId)?.ceilingFinish)
      if (list.length) {
        resolved.finishDefaultRooms = list
        resolved.finishDefaultSeparators = listRoomSeparators(model).filter((s) => s.levelId === resolved.levelId)
          .map((s) => ((s as { pts?: [number, number][] }).pts ?? [s.a, s.b]) as [number, number][])
        for (const r of list) if (!defaultMats.includes(r.materialId)) defaultMats.push(r.materialId)
      }
    }
    if (el.category === 'floor' && resolved.levelId) {
      const rooms = listRooms(model).filter((r) => r.levelId === resolved.levelId && r.finishes?.zones?.length)
      const out: NonNullable<typeof resolved.roomWallFinishes>['rooms'] = []
      for (const r of rooms) {
        const zones = normalizeRoomZones(r.finishes!.zones)
        const rz: (typeof out)[number]['zones'] = []
        let z0 = 0
        for (const z of zones) {
          const m = z.materialId ? getMaterial(model, z.materialId as MaterialId) : null
          if (m) {
            const c = claddingSpecOf(m)
            rz.push({
              z0Ft: z0, z1Ft: z.topFt, materialId: m.id,
              thicknessFt: Math.max(1 / 96, Math.min(0.25, c?.thicknessFt ?? 1 / 48)),
              tint: z.color ? surfaceHex(model, m.id, z.color) || null : null,
            })
          }
          if (z.topFt == null) break
          z0 = z.topFt
        }
        if (rz.length) out.push({ seedPt: [r.seedPt[0], r.seedPt[1]], zones: rz })
      }
      if (out.length) {
        resolved.roomWallFinishes = {
          rooms: out,
          separators: listRoomSeparators(model).filter((s) => s.levelId === resolved.levelId)
            .map((s) => ((s as { pts?: [number, number][] }).pts ?? [s.a, s.b]) as [number, number][]),
        }
      }
    }
    if (areas.length || defaultMats.length) {
      const th: Record<string, number> = {}
      for (const a of [...areas, ...defaultMats.map((materialId) => ({ materialId }))]) {
        if (th[a.materialId] != null) continue
        const m = getMaterial(model, a.materialId as MaterialId)
        const spec = el.category === 'floor' ? m?.floorFinish : m?.ceilingFinish
        if (spec && spec.thicknessFt > 0) th[a.materialId] = spec.thicknessFt
        // DECK PLANKS: a floor finish that builds as boards
        if (el.category === 'floor' && spec && m?.floorPlank && m.floorPlank.boardWidthFt > 0) {
          (resolved.slabFinishPlank ??= {})[a.materialId] = m.floorPlank
        } else if (spec && validFinishLayout(m?.floorLayout)) {
          // FINISH LAYOUT: a floor finish laid as real tiles / planks
          (resolved.slabFinishLayout ??= {})[a.materialId] = m!.floorLayout!
        }
      }
      resolved.slabFinishThicknessFt = th
      // FINISH COLORS (2026-10-07): an area's brushed color (else the finish's project standard)
      const tints: Record<string, string> = {}
      for (const a of areas as { id?: string; materialId: string; color?: unknown }[]) {
        if (!a.id) continue
        const tk = surfaceHex(model, a.materialId, a.color)
        if (tk) tints[a.id] = tk
      }
      if (Object.keys(tints).length) resolved.finishAreaTints = tints
    }
  }
  // STAIR TREAD FINISH: a floor-finish material picked as the stair's tread finish
  if (el.category === 'stair' && typeof resolved.params.treadFinish === 'string' && resolved.params.treadFinish) {
    const m = getMaterial(model, resolved.params.treadFinish as MaterialId)
    if (m?.floorFinish && m.floorFinish.thicknessFt > 0) {
      resolved.stairFinish = {
        materialId: m.id, thicknessFt: m.floorFinish.thicknessFt,
        ...(m.floorPlank && m.floorPlank.boardWidthFt > 0 ? { plank: m.floorPlank } : {}),
      }
    }
  }
  // RAKED WINDOW HEADS round: same paramLevels-style settings-map read, for windows
  if (el.category === 'window') {
    resolved.windowRake = normalizeWindowRake(maps(model).settings.get('windowRake'))
  }
  // PROJECT STYLE phase 2 (2026-10-06): the project's opening style folds into the effective
  // params (instance → type-if-stored → project), and the frame takes its palette colors —
  // Window & Door Exterior outside; a two-color system (clad, wood) Window & Door Interior
  // inside. Openings in exterior walls only (an interior door keeps its type's look).
  if (el.category === 'window' || el.category === 'door') {
    const host = el.hostId ? getElement(model, el.hostId) : null
    const hostType = host ? getType(model, host.typeId) : null
    const wallCat = getCategory('wall')
    const hostFn = hostType && wallCat ? resolveSchemaParams(wallCat.typeParams, hostType.params).params.function : null
    const hostExterior = hostFn === 'exterior'
    const styleSettings = normalizeOpeningStyle(maps(model).settings.get('openingStyle'))
    const follows = applyOpeningStyle(
      el.category, resolved.params, el.params as Record<string, ParamValue>, type.params as Record<string, ParamValue>,
      styleSettings, hostExterior,
    )
    // PROJECT STYLE phase 3: a door's condition + panel style (interior doors too)
    if (el.category === 'door') {
      const df = applyDoorStyle(
        resolved.params, el.params as Record<string, ParamValue>, type.params as Record<string, ParamValue>,
        resolved.typeParams as Record<string, ParamValue>, styleSettings.doorConditions, hostExterior,
        { interior: styleSettings.interiorDoorHeightFt, exterior: styleSettings.exteriorDoorHeightFt },
      )
      Object.assign(follows, df)
      // DOOR HARDWARE round (2026-10-09): the door's look (door → project → by construction),
      // finish (door → project → the look's own) and the pieces its set puts on it — one kit
      // every consumer reads (doorHardware.ts)
      {
        const ip = el.params as Record<string, ParamValue>
        const look = isHardwareLook(ip.hardwareLook) ? ip.hardwareLook
          : isHardwareLook(styleSettings.hardwareLook) ? styleSettings.hardwareLook
          : defaultHardwareLook(resolved.params.frameSystem)
        if (!isHardwareLook(ip.hardwareLook) && isHardwareLook(styleSettings.hardwareLook)) follows.hardwareLook = true
        const finish = isDoorHardwareFinish(ip.hardwareFinish) ? ip.hardwareFinish
          : isDoorHardwareFinish(styleSettings.hardwareFinish) ? styleSettings.hardwareFinish
          : hardwareLookSpec(look).finish
        resolved.params.hardwareLook = look
        resolved.params.hardwareFinish = finish
        resolved.params.__hw = resolveDoorHardware(resolved.params, String(resolved.typeParams.operation || 'hinged'), look, finish) as unknown as ParamValue
      }
      // DOOR LEAF round (2026-10-07): glass, construction and sidelights/transom are per DOOR
      // now. Mirror the resolved readings onto typeParams too, so every consumer that read
      // them off the type (protos, plan, elevation) keeps reading the one true value.
      const tp = resolved.typeParams as Record<string, ParamValue>
      for (const k of ['glazing', 'frameSystem', 'sidelightLeft', 'sidelightRight', 'transom'] as const) {
        tp[k] = resolved.params[k]
      }
      // PANEL STYLE PER GATE (2026-10-12): this gate's own style wins over its type's; a blank one
      // (the instance key shadows the type key) falls back to the type's
      if (isGateOp(String(tp.operation))) {
        const gs = (el.params as Record<string, ParamValue>).gateStyle
        if (typeof gs === 'string' && GATE_STYLE_VALUES.includes(gs)) tp.gateStyle = gs
        resolved.params.gateStyle = tp.gateStyle
      }
      if (resolved.params.glazing !== 'full') {
        for (const k of ['muntinPattern', 'muntinRows', 'muntinCols']) resolved.activeParamKeys.delete(k)
      }
    }
    // TRIM & EAVES: the project's interior trim + casing sizes (gates take none)
    {
      const ts = normalizeTrimStyle(maps(model).settings.get('trimStyle'))
      const gate = el.category === 'door' && isGateOp(resolved.typeParams.operation as string)
      Object.assign(follows, applyOpeningTrimStyle(resolved.params, el.params as Record<string, ParamValue>, type.params as Record<string, ParamValue>, ts, gate))
      // INTERIOR DOOR FRAMES: a door in an interior wall takes the project's jamb, finish face
      // to finish face (after the trim style, which decides cased or not)
      if (el.category === 'door' && !gate && hostFn === 'interior' && ts.intDoorJambFt != null) {
        // the host's RESOLVED thickness: a painted face (tile, paneling) is a real layer of it,
        // so the jamb deepens with it
        const hostT = el.hostId ? resolveElement(model, el.hostId)?.assemblyThicknessFt ?? 0 : 0
        if (hostT > 0 && applyInteriorDoorFrame(resolved.params, el.params as Record<string, ParamValue>, ts, hostT)) follows.interiorJamb = true
      }
    }
    // TYPICAL DOOR HEIGHTS: a window set by "door head height" lines its head up with the
    // project's door height for its wall (none set = its own head height field)
    if (el.category === 'window' && resolved.params.sillMode === 'doorHead') {
      const head = projectDoorHeightFt(styleSettings, hostExterior) ?? ((resolved.params.headHeightFt as number) || 6 + 8 / 12)
      resolved.params.headHeightFt = head
      resolved.params.sillFt = Math.max(0, head - ((resolved.params.heightFt as number) || 0))
    }
    if (Object.keys(follows).length) resolved.styleFollows = follows
    // TRIM COLORS (2026-10-07, Matt: "Painting window trims doesn't seem to work"): the casing /
    // sill / jamb lining the host wall builds around this opening wears the color brushed on
    // it — exterior trim and interior trim each their own (blank = its material's standard)
    {
      const pp = { ...resolved.typeParams, ...resolved.params } as Record<string, unknown>
      const extMat = typeof pp.trimMaterial === 'string' && pp.trimMaterial ? pp.trimMaterial : 'default'
      const intMat = typeof pp.interiorTrimMaterial === 'string' && pp.interiorTrimMaterial ? pp.interiorTrimMaterial : 'default'
      // TRIM & EAVES: painted trim (the default, or a paintable finish) takes its palette role —
      // exterior casings Trim, interior casings Interior Trim
      const paintable = (mid: string) => mid === 'default' || paintableFinish(getMaterial(model, mid as MaterialId))
      const ex = surfaceHex(model, extMat, resolved.params.trimColor, paintable(extMat) ? 'trim' : null)
      const ix = surfaceHex(model, intMat, resolved.params.interiorTrimColor, paintable(intMat) ? 'intTrim' : null)
      if (ex || ix) resolved.trimTints = { ...(ex ? { exterior: ex } : {}), ...(ix ? { interior: ix } : {}) }
    }
    // a GATE isn't a window or door frame: it takes only a color brushed on it (or its finish's
    // standard), never the Window & Door palette roles — it keeps its own steel / wood look
    const isGate = el.category === 'door' && isGateOp(resolved.typeParams.operation as string)
    if (hostExterior && !isGate) {
      const sm = resolved.typeParams.surfaceMaterial
      const mat = typeof sm === 'string' && sm ? sm : 'default'
      const ext = surfaceHex(model, mat, resolved.params.frameColorExterior, 'winExt')
      const int = frameSystemTwoColor(resolved.params.frameSystem)
        ? surfaceHex(model, mat, resolved.params.frameColorInterior, 'winInt')
        : ext
      // FRONT DOOR (phase 3): an entry door's own slab wears the Front Door color — brushed on
      // it (leafColor), else the palette's Front Door role
      const leaf = el.category === 'door' && resolved.params.doorCondition === 'entry'
        ? surfaceHex(model, mat, resolved.params.leafColor, 'frontDoor') : null
      if (ext || int || leaf) resolved.openingColors = { exterior: ext, interior: int, ...(leaf ? { leaf } : {}) }
    } else {
      // PAINT BRUSH ON OPENINGS (2026-10-07, Matt: "I need to be able to use the paint brush on
      // doors and windows"): an opening in an interior wall takes no palette role, but a color
      // brushed on it (or its finish's project standard) shows
      const sm = resolved.typeParams.surfaceMaterial
      const mat = typeof sm === 'string' && sm ? sm : 'default'
      const ext = surfaceHex(model, mat, resolved.params.frameColorExterior)
      const int = frameSystemTwoColor(resolved.params.frameSystem)
        ? surfaceHex(model, mat, resolved.params.frameColorInterior)
        : ext
      if (ext || int) resolved.openingColors = { exterior: ext, interior: int }
    }
  }
  // WALL FRAMING round: materialize the structural-core layer's MaterialRecord (the
  // wood-core signal for the framing derivation) — same no-model-handle rationale as
  // paramLevels/paramTypes above. Any assembly-backed element gets it; a dangling
  // material id simply leaves it absent (consumers treat absent as non-wood).
  if (assembly && assembly.layers.length) {
    const coreIdx = Math.min(Math.max(assembly.coreBoundary?.start ?? 0, 0), assembly.layers.length - 1)
    const coreMat = getMaterial(model, assembly.layers[coreIdx].materialId)
    if (coreMat) resolved.coreMaterial = coreMat
  }
  // BARREL-TILE ROOFING (docs/bim-plan/12): materialize the seeded tile-material
  // ids by NAME onto roofs that declare a roofingProfile, so the tile generator
  // reads real ids with no model handle (the coreMaterial rationale). Missing
  // records simply leave the field partial — the generator degrades to the
  // assembly finish material rather than failing.
  if (el.category === 'roof') {
    const byName = new Map(listMaterials(model).map((m) => [m.name, m.id]))
    // PAINTED ROOFING: the spec of every finish painted on this roof's planes
    const specs: Record<string, import('./types').RoofingSpec> = {}
    const bnodes = (resolved.params.boundary as { nodes?: { edge?: { finish?: string } }[] } | null)?.nodes || []
    for (const n of bnodes) {
      const fid = n.edge?.finish
      if (fid && !specs[fid]) {
        const sp = roofingSpecOf(getMaterial(model, fid as MaterialId))
        // SHINGLE ROOFING: the blend shades by name → ids (no model handle downstream)
        specs[fid] = sp.blendNames?.length && !sp.blendIds?.length
          ? { ...sp, blendIds: sp.blendNames.map((nm) => byName.get(nm)).filter((v): v is MaterialId => !!v) }
          : sp
      }
    }
    resolved.roofingSpecs = specs
    const blends = ROOF_TILE_MATERIAL_NAMES.blends
      .map((nm) => byName.get(nm))
      .filter((v): v is MaterialId => !!v)
    resolved.roofTileMaterials = {
      blends,
      mortar: byName.get(ROOF_TILE_MATERIAL_NAMES.mortar),
      // METALS ROLE: the tile roof's valley + wall flashing wears the Metals color
      flashing: ((f) => (f ? ((metalsBareMaterial(model) ?? tinted(f, metalsHex(model, f, null))) as MaterialId) : f))(byName.get(ROOF_TILE_MATERIAL_NAMES.flashing)),
    }
  }
  // WALL END FINISHES (2026-10-06): each free end's finish + its built thickness (bed included)
  if (el.category === 'wall') {
    const ends: NonNullable<typeof resolved.endFinishes> = {}
    for (const [which, key] of [['start', 'finishStart'], ['end', 'finishEnd']] as const) {
      const id = resolved.params[key]
      if (typeof id !== 'string' || !id) continue
      const spec = claddingSpecOf(getMaterial(model, id as MaterialId))
      if (!spec) continue
      ends[which] = {
        materialId: id, thicknessFt: spec.thicknessFt + (spec.bed && spec.bed.thicknessFt > 0 ? spec.bed.thicknessFt : 0),
        spec: spec.profile === 'lap' ? { ...spec, ...lapCornerOf(model) } : spec,
      }
    }
    if (ends.start || ends.end) resolved.endFinishes = ends
  }
  // COLORS (2026-10-05): what each surface SHOWS over its finish — the brushed color, else
  // the project standard for that finish, else the finish's own color (absent). Resolved here
  // so the generators need no model handle (the claddingSpecs precedent).
  if (el.category === 'wall' && resolved.assembly && resolved.assembly.layers.length) {
    const layers = resolved.assembly.layers
    const tints: Record<string, string> = {}
    // PROJECT STYLE (2026-10-06): a PAINTABLE finish with no brush and no standard takes its
    // surface's palette role — exterior face Field, interior face Interior Walls, bands Trim
    const put = (mat: string | null | undefined, brushed: unknown, role: PaletteRoleId | null) => {
      if (!mat) return
      const r = role && paintableFinish(getMaterial(model, mat as MaterialId)) ? role : null
      const hex = surfaceHex(model, mat, brushed, r)
      if (hex) tints[mat] = hex
    }
    // end finishes and bands take their finish's standard; then interior; the exterior wins
    // (wall bands / copings in a finish of their own: that finish's standard — r3, so a band
    // or coping in the wall's own brick shows exactly the wall's color, set by the faces below)
    for (const sw of resolved.faceSweeps || []) put(sw.mat, null, null)
    if (resolved.copingMats) { put(resolved.copingMats.main, null, null); put(resolved.copingMats.tile, null, null) }
    for (const e of Object.values(resolved.endFinishes || {})) put(e?.materialId, null, 'field')
    for (const side of ['interior', 'exterior'] as const) {
      // BAND COLORS (2026-10-07): a band wears its own brushed color; else its role
      for (const b of faceBands(resolved.params, side)) put(b.materialId, b.color ?? null, side === 'exterior' ? 'field' : 'intWalls')
    }
    put(layers[layers.length - 1].materialId as string, resolved.params.colorInterior, 'intWalls')
    put(layers[0].materialId as string, resolved.params.colorExterior, 'field')
    if (Object.keys(tints).length) resolved.matTints = tints
  }
  if (el.category === 'roof') {
    const bn = (resolved.params.boundary as { nodes?: { edge?: { finish?: unknown; color?: unknown } }[] } | null)?.nodes || []
    const cols = bn.map((n) => {
      const fin = typeof n.edge?.finish === 'string' && n.edge.finish ? n.edge.finish : null
      return fin ? surfaceHex(model, fin, n.edge?.color) : null
    })
    if (cols.some((c) => c)) resolved.planeColors = cols
    const core = resolved.coreMaterial?.id as string | undefined
    const mc: Partial<Record<'tails' | 'outlookers' | 'barge', string>> = {}
    for (const [k, key] of [['tails', 'tailColor'], ['outlookers', 'outlookerColor'], ['barge', 'bargeColor']] as const) {
      const hex = surfaceHex(model, core, resolved.params[key], k === 'barge' && paintableFinish(core ? getMaterial(model, core as MaterialId) : null) ? 'trim' : null)
      if (hex) mc[k] = hex
    }
    if (Object.keys(mc).length) resolved.roofMemberColors = mc
    // FASCIA + SOFFIT COLOR (2026-10-07, Matt: "Just make it so soffits and fascias receive the
    // same color as Trims as they are set in Palette/Exterior … Allow us to apply paints or stains
    // to them"): the fascia and the eave underside (boxed / flat soffit, or the decking between
    // open rafter tails) take a brushed color (`fasciaColor` / `soffitColor`), else the palette's
    // Trim role when the finish can wear it. The framing wood takes paint or stain.
    {
      const a = resolved.assembly
      const framing = a && a.layers.length
        ? (a.layers[Math.min(Math.max(a.coreBoundary?.start ?? 0, 0), a.layers.length - 1)].materialId as string) ?? 'default'
        : 'default'
      const pick = (k: string) => (typeof resolved.params[k] === 'string' && resolved.params[k] ? resolved.params[k] as string : null)
      const fasciaBase = pick('fasciaMaterial') ?? framing
      const soffitBase = pick('deckingMaterial') ?? framing
      const fh = surfaceHex(model, fasciaBase, resolved.params.fasciaColor, 'trim')
      const sh = surfaceHex(model, soffitBase, resolved.params.soffitColor, 'trim')
      resolved.eaveColors = { fascia: fh, soffit: sh, fasciaBase, soffitBase }
    }
    // GUTTERS + DOWNSPOUTS (2026-10-07, Matt: "the gutters and downspouts still look like they
    // are yellow … they should be responsive to the new general settings"): a blank gutter
    // material used to fall back to the roof's own WOOD framing material (the brass look). It
    // is the firm's painted-aluminum gutter now, and it takes the palette's Metals color (a
    // role reaches it only if it fits — a RAL coating does; copper picked here stays copper).
    {
      const picked = typeof resolved.params.gutterMaterial === 'string' && resolved.params.gutterMaterial ? resolved.params.gutterMaterial : null
      resolved.gutterMat = gutterMaterialOf(model, picked, resolved.params.gutterColor)
    }
  }
  // METALS ROLE (2026-10-07): a downspout with its own material, and a railing's metal parts
  // (posts + rails, or a glass railing's shoe), wear the palette's Metals color. A downspout
  // with no material of its own follows its gutter (gutterMat, above). Shower curbs are
  // interior fixtures, not railings — they keep their own look.
  if (el.category === 'downspout') {
    const m = typeof resolved.typeParams.material === 'string' && resolved.typeParams.material ? resolved.typeParams.material : null
    // a downspout brushed its own color (PAINT BRUSH, 2026-10-07) stops following its gutter's
    const brushed = resolved.params.paintColor
    const base = m ?? (brushed ? listMaterials(model).find((x) => x.firmId === GUTTER_MATERIAL_FIRM_ID || x.name === GUTTER_MATERIAL_NAME)?.id ?? 'default' : null)
    if (base) resolved.downspoutMat = tinted(base, metalsHex(model, base, brushed))
  }
  if (el.category === 'railing' && resolved.params.railStyle !== 'showerCurb') {
    const style = resolved.params.railStyle
    const key = style === 'glassShoe' ? 'shoeMaterial' : 'surfaceMaterial'
    const v = resolved.params[key] ?? resolved.typeParams[key]
    const m = typeof v === 'string' && v ? v : null
    // BARE METALS: an unpicked railing is made of the Metals role's bare metal
    const bare = m ? null : metalsBareMaterial(model)
    if (bare) resolved.metalMat = bare
    const hex = metalsHex(model, m ?? bare, resolved.params.paintColor)
    if (hex) resolved.memberColor = hex
  }
  if (el.category === 'column' || el.category === 'beam') {
    const mat = typeof resolved.params.material === 'string' && resolved.params.material ? resolved.params.material : null
    const hex = surfaceHex(model, mat, resolved.params.paintColor)
    if (hex) resolved.memberColor = hex
  }
  // DECORATIVE BOX BEAMS (2026-10-08, boxBeams.ts): the boards' finish — the ceiling's own pick,
  // else the project's standard, else plain wood — tinted by the brushed color, else the finish's
  // own standard, else the palette's Box Beams role; the nailer inside is sawn lumber.
  // A firm finish the model doesn't have yet still shows its wood color (on 'default').
  if (el.category === 'ceiling' && resolved.params.boxBeams === true) {
    const ts = normalizeTrimStyle(maps(model).settings.get('trimStyle'))
    const own = typeof resolved.params.boxBeamMaterial === 'string' && resolved.params.boxBeamMaterial ? resolved.params.boxBeamMaterial : null
    const pick = own ?? ts.boxBeamFinish
    let base = 'default'
    let fallbackHex: string | null = null
    if (pick && getMaterial(model, pick as MaterialId)) base = pick
    else {
      const fin = boxBeamFinishOf(pick) ?? DEFAULT_BOX_BEAM_FINISH
      const id = boxBeamFinishMaterialId(model, fin)
      if (id) base = id
      else fallbackHex = fin.color
    }
    const brushed = resolved.params.boxBeamColor || null
    const hex = base === 'default'
      ? (surfaceHex(model, null, brushed || 'role:boxBeams') ?? fallbackHex)
      : surfaceHex(model, base, brushed, 'boxBeams')
    const nailer = listMaterials(model).find((x) => x.name === 'Sawn Lumber')?.id ?? base
    resolved.boxBeamMats = { board: tinted(base, hex), boardBase: base, nailer }
  }
  // COLORS (2026-10-06): a cabinet's color over its surface finish (fronts + box follow it)
  if (el.category === 'casework') {
    const sm = resolved.typeParams.surfaceMaterial
    const mat = typeof sm === 'string' && sm ? sm : null
    const hex = mat ? surfaceHex(model, mat, resolved.params.paintColor, paintableFinish(getMaterial(model, mat as MaterialId)) ? 'cabinets' : null) : null
    if (hex) resolved.memberColor = hex
  }
  // LIGHT STYLES (2026-10-07): a styled light's metal (or plaster) body wears the color
  // brushed on it, else the project standard for light-fixture metal — over the shared Light
  // Fixture finish material. Nothing set = the style's own finish (the 3D builder's default).
  if (el.category === 'fixture') {
    const kind = resolved.typeParams.fixtureKind
    const style = lightStyleOf(kind, { ...resolved.typeParams, ...resolved.params })
    if (style) {
      const hex = surfaceHex(model, lightBodyMaterialId(style.body), resolved.params.paintColor)
      if (hex) resolved.memberColor = hex
    }
    // HANG HEIGHT (2026-10-08, Matt: "set the height to the bottom of the fixture from the host
    // level … then the suspension length automatically adjusts"): a hanging light with a Bottom
    // Height derives its drop from the ceiling it hangs from — everything downstream (3D, pick
    // box, light source, sections) reads dropFt, so it is set here, once
    const bottom = resolved.params.bottomHeightFt
    if (style && HANGING_LIGHT_KINDS.includes(String(kind)) && typeof bottom === 'number' && isFinite(bottom) && resolved.level) {
      const host = el.hostId ? resolveElement(model, el.hostId as ElementId) : null
      const ceilingZ = host && host.category === 'ceiling' && host.level
        ? host.level.elevationFt + ((host.params.heightOffsetFt as number) || 0)
        : resolved.level.elevationFt + UNHOSTED_CEILING_LIGHT_FT
      const drop = ceilingZ - (resolved.level.elevationFt + bottom)
      resolved.params.dropFt = Math.max(drop, (style.z1 - style.z0) / 12)
    }
  }
  // COLORS (2026-10-06): a fence's frame and infill colors
  if (el.category === 'fence') {
    const tp = resolved.typeParams
    const frame = typeof tp.surfaceMaterial === 'string' && tp.surfaceMaterial ? tp.surfaceMaterial : null
    const infill = typeof tp.infillMaterial === 'string' && tp.infillMaterial ? tp.infillMaterial : frame
    const fh = surfaceHex(model, frame, resolved.params.frameColor)
    const ih = surfaceHex(model, infill, resolved.params.infillColor)
    if (fh || ih) resolved.fenceColors = { ...(fh ? { frame: fh } : {}), ...(ih ? { infill: ih } : {}) }
  }
  // WINDOW HEAD/SILL round (2026-08-18, Matt): sillMode chooses which of
  // sillFt/headHeightFt is the stored/authoritative field (the topMode/stemMode
  // precedent) — when it's 'head', OVERRIDE the resolved `sillFt` here so every
  // existing consumer downstream (they all just read `params.sillFt`) sees the
  // correct effective value with no changes of their own. Clamped to the
  // param's own declared min (0) — a head height shorter than the window is
  // an invalid combination, not a negative sill.
  if (el.category === 'window' && resolved.params.sillMode === 'head') {
    const headHeightFt = (resolved.params.headHeightFt as number) || 0
    const heightFt = (resolved.params.heightFt as number) || 0
    resolved.params.sillFt = Math.max(0, headHeightFt - heightFt)
  }
  // SHOWER PAN SIZE (2026-09-30): a shower's own pan width/depth (instance params,
  // 0 = the type's) override the type size HERE, in the resolved typeParams every
  // consumer reads — the sidelight-migration precedent below of writing through
  // resolved.typeParams. Unset values read back as the type size, so the palette
  // shows the real dimension rather than 0.
  // FURNITURE (2026-09-30): the same fold for every sized furniture kind, and the bed's
  // footprint DERIVED from its mattress size + frame allowance (never typed by hand)
  if (el.category === 'object') applyObjectInstanceSize(resolved.typeParams, resolved.params)
  // PLUMBING STYLES (2026-10-07): a styled fixture's metal wears the color brushed on it, else
  // the project standard for plumbing metal (nothing = the style's own finish)
  if (el.category === 'object' && isStyledPlumbingKind(resolved.typeParams.objectKind)) {
    // OUTDOOR GRILLS: a grill wears only its own brushed color — never the project's
    // plumbing-metal standard (a brass bath standard must not gild the barbecue)
    const grill = resolved.typeParams.objectKind === 'outdoorGrill'
    const hex = surfaceHex(model, grill ? null : PLUMBING_METAL_MATERIAL_ID, resolved.params.paintColor)
    if (hex) resolved.memberColor = hex
  }
  // SITE FURNISHINGS (2026-10-06, Matt: "allow me to toggle off the netting"): a
  // trampoline's safety net is per placement — folded into the type params so the
  // 3D body and its instancing key both follow it
  // PANEL COUNT PER INSTANCE (2026-10-06): a multi-panel door's own panel count wins over
  // the type's starting count — folded into the type params (the instancing key, the
  // proto, the plan symbol, elevation figures and schedules all read it there) and back
  // into params so the palette shows the count in effect, never a blank
  if (el.category === 'door') {
    for (const k of ['panelCount', 'panelCountB'] as const) {
      const own = resolved.params[k]
      if (typeof own === 'number' && Number.isFinite(own)) resolved.typeParams[k] = Math.round(own)
      if (resolved.typeParams[k] !== undefined) resolved.params[k] = resolved.typeParams[k]
    }
  }
  // FREESTANDING TUB (2026-10-06): the floor-mounted filler is per placement, too
  if (el.category === 'object' && resolved.typeParams.objectKind === 'tubFreestanding') {
    resolved.typeParams.tubFiller = resolved.params.tubFiller !== false
  }
  // SITE UTILITIES (2026-10-07): the gas meter's earthquake valve is per placement, too
  if (el.category === 'object' && resolved.typeParams.objectKind === 'gasMeter') {
    resolved.typeParams.gasSeismicValve = resolved.params.gasSeismicValve === true
  }
  if (el.category === 'object' && String(resolved.typeParams.objectKind).startsWith('trampoline')) {
    resolved.typeParams.trampolineNet = resolved.params.trampolineNet !== false
  }
  // RESIDENTIAL ELEVATOR: the travel — bottom stop (its own level) to the top-stop
  // level (picked, else the next level up, else 10'), folded into the type params so
  // the 3D body and its instancing key both follow it
  if (el.category === 'object' && resolved.typeParams.objectKind === 'elevator') {
    const own = level?.elevationFt ?? 0
    let top = resolved.paramLevels?.elevatorTopLevelId ?? null
    if (!top) {
      const above = listLevels(model).filter((l) => l.elevationFt > own + 1e-6).sort((a, b) => a.elevationFt - b.elevationFt)
      top = above[0] ?? null
    }
    resolved.typeParams.elevatorTravelFt = top ? Math.max(0, top.elevationFt - own) : 10
    resolved.typeParams.elevatorCabAt = resolved.params.elevatorCabAt === 'top' ? 'top' : 'bottom'
    // RESIDENTIAL ELEVATOR WIRING (2026-10-06): every level it serves, bottom to top (a
    // residential elevator stops at each floor it passes) — hookups.ts reads these for
    // the top-landing controller and a hall call station at each landing door. An
    // instance param, never the type, so the 3D body's instancing key is untouched.
    const topZ = top ? top.elevationFt : own
    resolved.params.elevatorStops = listLevels(model)
      .filter((l) => l.id === el.levelId || (l.elevationFt > own + 1e-6 && l.elevationFt <= topZ + 1e-6))
      .sort((a, b) => a.elevationFt - b.elevationFt)
      .map((l) => ({ id: l.id, name: l.name, elevationFt: l.elevationFt })) as unknown as ParamValue
    // ELEVATOR RIDE (2026-10-20): the car's button panel has a button per stop, so the
    // count shapes the body — a type param, so the instancing key follows it
    resolved.typeParams.elevatorStopCount = Math.max(2, (resolved.params.elevatorStops as unknown as unknown[]).length)
  }
  // SIDELIGHTS PER-SIDE round (2026-08-19): every door TYPE saved before this round
  // still carries the old `sidelight`/`sidelightWidthFt` pair (both sides, one
  // width) in its raw stored params — the schema no longer declares them, so
  // resolveSchemaParams silently drops them. Migrate here, the sillMode
  // precedent: read the RAW type record (never the schema-resolved output, which
  // has no knowledge of a retired key) and, only when the type never declared
  // either new per-side param, fan the old symmetric flag out to both —
  // preserving every existing door's built geometry with no data migration step.
  // (Belt-and-suspenders: `ensureDefaultLibrary`'s `migrateDoorSidelights` already
  // converts this at doc-open in the normal app flow — this covers any consumer
  // that resolves a model without going through that, e.g. a headless test.)
  // SITE GATES (openingOps.ts GATE_OPS): a sliding gate runs off its host wall's FACE,
  // so its prototype needs the host's thickness — the elevator/paramLevels no-model-
  // handle pattern. The wall generator re-stamps its exact (painted) thickness on its
  // own opening copy; this one covers walk mode and the settings previews.
  if (el.category === 'door' && el.hostId && isGateOp(resolved.typeParams.operation as string)) {
    const hostEl = getElement(model, el.hostId)
    const hostType = hostEl ? getType(model, hostEl.typeId) : null
    const hostAsm = hostType?.assemblyId ? getAssembly(model, hostType.assemblyId) : null
    if (hostAsm) resolved.params.__hostT = assemblyThickness(hostAsm)
  }
  if (el.category === 'door') {
    const raw = type.params as Record<string, ParamValue>
    if (raw.sidelight === true && raw.sidelightLeft === undefined && raw.sidelightRight === undefined) {
      const w = (raw.sidelightWidthFt as number) || 1.25
      resolved.typeParams.sidelightLeft = true
      resolved.typeParams.sidelightRight = true
      resolved.params.sidelightLeft = true
      resolved.params.sidelightRight = true
      // width moved to INSTANCE params (2026-08-19 follow-up, "editable like the
      // size of the doors") — only `resolved.params` (never typeParams) is
      // meaningful for these now. This branch only fires for the truly ancient
      // single-bool format, which by definition never had a real per-side width
      // of its own, so overwriting resolveSchemaParams' plain schema-default here
      // (never a real user value in this branch) is correct, not lossy.
      resolved.params.sidelightLeftWidthFt = w
      resolved.params.sidelightRightWidthFt = w
    }
  }
  return resolved
}

export function resolveAllElements(model: ModelDoc): ResolvedElement[] {
  const build = (): ResolvedElement[] => {
    const out: ResolvedElement[] = []
    maps(model).elements.forEach((_v, k) => {
      const r = resolveElement(model, k as ElementId)
      if (r) out.push(r)
    })
    return out
  }
  if (inTransaction(model)) return build()
  // PLAN PERF round: one full resolve per doc version (and set signature), shared by every consumer
  const c = cacheBucket(model, readCache(model))
  if (!c.resolvedAll) c.resolvedAll = build()
  return c.resolvedAll.slice()
}

// ---------- lifecycle ----------

export function snapshotModel(model: ModelDoc): Uint8Array {
  return Y.encodeStateAsUpdate(model.ydoc)
}

/** Open an existing model from a snapshot (headless consumers use this too). */
export function docFromSnapshot(snapshot: Uint8Array): ModelDoc {
  const ydoc = new Y.Doc()
  Y.applyUpdate(ydoc, snapshot)
  return { ydoc, origin: {} }
}

/**
 * SAVE MERGE round: fold a SAVED snapshot (another session's full doc state) into this
 * live doc — the CRDT union, so both sides' edits survive and nothing is overwritten.
 * Applied under `origin` (callers pass the sync REMOTE_ORIGIN) so undo and local-dirty
 * tracking skip it by construction, exactly like a live peer update.
 */
export function mergeSnapshot(model: ModelDoc, snapshot: Uint8Array, origin: unknown): void {
  Y.applyUpdate(model.ydoc, snapshot, origin)
}

/**
 * ONE-SAVER round: does a SAVED snapshot already hold everything in this live doc —
 * inserts AND deletes? True = a teammate's save covered this tab's edits, so it has
 * nothing of its own to upload. Replays this doc onto a scratch copy of the snapshot
 * and reports whether anything changed (Yjs emits no update when every struct and
 * deletion is already present — pinned by saveMerge.test.ts).
 */
export function snapshotCovers(model: ModelDoc, snapshot: Uint8Array): boolean {
  const scratch = new Y.Doc()
  Y.applyUpdate(scratch, snapshot)
  let changed = false
  scratch.on('update', () => { changed = true })
  Y.applyUpdate(scratch, Y.encodeStateAsUpdate(model.ydoc))
  scratch.destroy()
  return !changed
}

/** Brand-new empty doc (no seeding — see createSeededModel in seed.ts). */
export function createModelDoc(meta: { conceptId: string; projectId: string }): ModelDoc {
  const ydoc = new Y.Doc()
  const model: ModelDoc = { ydoc, origin: {} }
  // Meta writes use a DIFFERENT origin than the session origin so they never enter undo.
  ydoc.transact(() => {
    const m = maps(model).meta
    m.set('schemaVersion', 1)
    m.set('conceptId', meta.conceptId)
    m.set('projectId', meta.projectId)
    m.set('units', 'ft')
  })
  return model
}

/** LAP SIDING: the project siding settings folded into a lap finish's resolved spec */
function lapCornerOf(model: ModelDoc): Pick<import('./types').CladdingSpec, 'corner' | 'cornerBoardWidthFt' | 'cornerBoardThickFt'> {
  const st = normalizeSidingSettings(maps(model).settings.get('siding'))
  return { corner: st.corner, cornerBoardWidthFt: st.cornerBoardWidthFt, cornerBoardThickFt: st.cornerBoardThickFt }
}
function withSidingSettings<T extends Partial<Record<'exterior' | 'interior', { materialId: string; spec: import('./types').CladdingSpec }>>>(model: ModelDoc, specs: T): T {
  const out = { ...specs }
  for (const side of ['exterior', 'interior'] as const) {
    const c = out[side]
    if (c && c.spec.profile === 'lap') (out as Record<string, unknown>)[side] = { ...c, spec: { ...c.spec, ...lapCornerOf(model) } }
  }
  return out
}
